import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HiPencil, HiEye, HiPlus, HiX, HiFilter, HiSearch, HiCurrencyRupee, HiCash, HiDocumentReport, HiCheck, HiPrinter } from 'react-icons/hi';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useNotification } from '../context/NotificationContext';
import ConfirmationModal from '../components/ConfirmationModal';
import AccountSelect from '../components/AccountSelect';
import CitySelect from '../components/CitySelect';
import Tooltip from '../components/Tooltip';
import { getErrorMessage } from '../utils/errorHandler';
import { formatDate } from '../utils/dateFormatter';
import { formatPKR } from '../utils/currencyFormatter';
import { API } from '../config';
import axios from 'axios';
import { addPdfBrandLogo } from '../utils/pdfLogo';
import { downloadPartyReceivingReceiptPdf } from '../utils/partyReceivingReceiptPdf';
import './Parties.css';
import './Accounts.css';

const PDF_BUSINESS_NAME = 'Safari Club';

/** Top-level and per-split reference numbers on payment ledger meta (snake/camel from API). */
function getPaymentReferenceFromMeta(meta) {
  if (!meta || typeof meta !== 'object') return '';
  const top = (meta.referenceNumber ?? meta.reference_number ?? '').toString().trim();
  const splits = meta.paymentSplits || meta.payment_splits;
  const fromSplits = (Array.isArray(splits) ? splits : [])
    .map((s) => (s.referenceNumber ?? s.reference_number ?? '').toString().trim())
    .filter(Boolean);
  return [...new Set([top, ...fromSplits].filter(Boolean))].join(', ');
}

/** Payment / Cheque column for statement print & PDF. */
function getStatementPaymentMethodText(meta) {
  const m = meta || {};
  const pm = m.paymentMethod ?? m.payment_method;
  const chequeNum = m.chequeNumber ?? m.cheque_number;
  let s = '—';
  if (pm === 'cheque') s = chequeNum ? `Cheque #${chequeNum}` : 'Cheque';
  else if (pm === 'cash') s = 'Cash';
  else if (pm === 'split' && (m.paymentSplits || m.payment_splits)?.length) s = 'Split';
  else if (pm) s = String(pm);
  const ref = getPaymentReferenceFromMeta(m);
  if (ref) s = s === '—' ? `Ref: ${ref}` : `${s} · Ref: ${ref}`;
  return s;
}

const Parties = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEditPartyBalance = user?.role === 'super_admin' || user?.role === 'admin';
  const { hasBranches, effectiveBranchId, isAdmin, currentBranch } = useBranch();
  const { success, error } = useNotification();
  const [parties, setParties] = useState([]);
  const [filteredParties, setFilteredParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [statementParty, setStatementParty] = useState(null);
  const [statementFrom, setStatementFrom] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [statementTo, setStatementTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [statementData, setStatementData] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [ledgerReceiptDownloadingId, setLedgerReceiptDownloadingId] = useState(null);
  const paymentSubmitInFlightRef = useRef(false);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUpdateConfirmModal, setShowUpdateConfirmModal] = useState(false);
  const [partyToDelete, setPartyToDelete] = useState(null);
  const [editingParty, setEditingParty] = useState(null);
  const [selectedParty, setSelectedParty] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerHasMore, setLedgerHasMore] = useState(false);
  const [ledgerLoadingMore, setLedgerLoadingMore] = useState(false);
  const [reversingLedgerId, setReversingLedgerId] = useState(null);
  const [showPartyAuditModal, setShowPartyAuditModal] = useState(false);
  const [partyAuditLoading, setPartyAuditLoading] = useState(false);
  const [partyAuditEntries, setPartyAuditEntries] = useState([]);
  const [partyCheques, setPartyCheques] = useState([]);
  const [partyChequesLoading, setPartyChequesLoading] = useState(false);
  const [partyChequesTotal, setPartyChequesTotal] = useState(0);
  const [partyChequesHasMore, setPartyChequesHasMore] = useState(false);
  const [partyChequesLoadingMore, setPartyChequesLoadingMore] = useState(false);
  const [statementPdfLoading, setStatementPdfLoading] = useState(false);
  const [partyChequesSortBy, setPartyChequesSortBy] = useState('date');
  const [partyChequesSortOrder, setPartyChequesSortOrder] = useState('desc');
  const [chequeToApprove, setChequeToApprove] = useState(null);
  const [approveSplits, setApproveSplits] = useState([{ accountId: '', amount: '' }]);
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    paymentType: 'payment_out',
    paymentSplits: [{ paymentMethod: 'cash', accountId: '', amount: '', chequeNumber: '', chequeDate: '', referenceNumber: '' }],
    description: '',
    transactionDate: new Date().toISOString().split('T')[0]
  });
  const [fundAccounts, setFundAccounts] = useState([]);
  const [salaryFormData, setSalaryFormData] = useState({
    amount: '',
    accountId: '',
    periodMonth: '',
    periodYear: '',
    description: '',
    paymentMethod: 'cash',
    transactionDate: new Date().toISOString().split('T')[0]
  });
  const [advanceFormData, setAdvanceFormData] = useState({
    amount: '',
    accountId: '',
    description: '',
    paymentMethod: 'cash',
    transactionDate: new Date().toISOString().split('T')[0]
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [partiesPdfLoading, setPartiesPdfLoading] = useState(false);
  const [vendorPurchases, setVendorPurchases] = useState([]);
  const [showPurchaseSelectModal, setShowPurchaseSelectModal] = useState(false);
  const [loadingVendorPurchases, setLoadingVendorPurchases] = useState(false);
  const [selectedPurchaseForReturn, setSelectedPurchaseForReturn] = useState(null);
  const [returnFormData, setReturnFormData] = useState({ returnDate: new Date().toISOString().split('T')[0], items: [], reason: '', notes: '' });
  const [returnSearchTerm, setReturnSearchTerm] = useState('');
  const [returnCart, setReturnCart] = useState([]);
  const [returnCartEditIndex, setReturnCartEditIndex] = useState(null);
  const [returnQtyErrors, setReturnQtyErrors] = useState({});
  const [showReturnFormModal, setShowReturnFormModal] = useState(false);
  const [loadingPurchaseDetail, setLoadingPurchaseDetail] = useState(false);
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(user.business_id || '');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    address: '',
    bankAccount: '',
    bankName: '',
    isVendor: false,
    isCustomer: false,
    isTailor: false,
    isAdditional: false,
    isEmployee: false,
    isShopExpense: false,
    salary: '',
    notes: '',
    openingBalanceDirection: '', // 'they_owe_you' | 'you_owe_them'
    openingBalanceAmount: '',
    balanceEditReason: '',
    employeeAdvance: '',
    salaryPaidForCurrentMonth: false
  });

  // Fetch businesses for super_admin
  useEffect(() => {
    if (user.role === 'super_admin') {
      fetchBusinesses();
    }
  }, [user.role]);

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/businesses/all`);
      const businessList = response.data.businesses || [];
      setBusinesses(businessList);
      if (!selectedBusinessId && businessList.length > 0) {
        setSelectedBusinessId(businessList[0].id);
      }
    } catch (err) {
      error('Failed to fetch businesses');
    }
  };

  useEffect(() => {
    if (selectedBusinessId) {
      fetchParties();
    }
  }, [roleFilter, selectedBusinessId, effectiveBranchId]);

  // Close Add Party modal when there are no branches (everything is branch-attached)
  useEffect(() => {
    if (!hasBranches && showModal && !editingParty) {
      setShowModal(false);
      resetForm();
    }
  }, [hasBranches]);

  // Auto-select opening balance direction by role (add party only; all remain editable)
  useEffect(() => {
    if (editingParty) return;
    let direction = '';
    if (formData.isVendor) direction = 'you_owe_them';
    else if (formData.isCustomer) direction = 'they_owe_you';
    else if (formData.isTailor) direction = 'you_owe_them'; // Tailors: you owe them (payable) by default
    setFormData(prev => (prev.openingBalanceDirection === direction ? prev : { ...prev, openingBalanceDirection: direction }));
  }, [formData.isVendor, formData.isCustomer, formData.isTailor, editingParty]);

  useEffect(() => {
    const bid = user?.role === 'super_admin' ? selectedBusinessId : (user?.business_id || user?.businessId);
    if (bid) {
      axios.get(`${API}/funds/business/${bid}/accounts`)
        .then(res => setFundAccounts(res.data.accounts || []))
        .catch(() => setFundAccounts([]));
    }
  }, [selectedBusinessId, user?.business_id, user?.businessId, user?.role]);

  // Auto-select default cash account when modal opens and accounts are loaded
  useEffect(() => {
    if (showPaymentModal && fundAccounts.length > 0 && paymentFormData.paymentSplits.length > 0) {
      const defaultCashAccount = fundAccounts.find(a => a.type === 'cash');
      const firstSplit = paymentFormData.paymentSplits[0];
      // If first split is cash and has no account selected, set default cash account
      if (firstSplit.paymentMethod === 'cash' && !firstSplit.accountId && defaultCashAccount) {
        const newSplits = [...paymentFormData.paymentSplits];
        newSplits[0] = { ...newSplits[0], accountId: String(defaultCashAccount.id) };
        setPaymentFormData({ ...paymentFormData, paymentSplits: newSplits });
      }
    }
  }, [showPaymentModal, fundAccounts.length]);

  useEffect(() => {
    if (!showPaymentModal) {
      paymentSubmitInFlightRef.current = false;
      setPaymentSubmitting(false);
    }
  }, [showPaymentModal]);

  useEffect(() => {
    filterParties();
  }, [searchTerm, parties]);

  const openLedgerForPartyId = location.state?.openLedgerForPartyId;
  useEffect(() => {
    if (!openLedgerForPartyId) return;
    axios.get(`${API}/parties/${openLedgerForPartyId}`)
      .then(res => {
        const party = res.data.party;
        if (party) {
          setSelectedParty(party);
          setShowLedgerModal(true);
          fetchLedger(party.id);
        }
        navigate(location.pathname, { replace: true, state: {} });
      })
      .catch(() => navigate(location.pathname, { replace: true, state: {} }));
  }, [openLedgerForPartyId]);

  const filterParties = () => {
    let filtered = parties.filter(party => !party.is_employee);
    if (searchTerm) {
      filtered = filtered.filter(party =>
        party.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        party.phone?.includes(searchTerm) ||
        party.city?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredParties(filtered);
  };

  const fetchParties = async () => {
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
      if (!businessId) {
        setLoading(false);
        return;
      }
      let url = `${API}/parties/business/${businessId}`;
      const params = new URLSearchParams();
      if (roleFilter) params.set('role', roleFilter);
      if (effectiveBranchId) params.set('branchId', effectiveBranchId);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url);
      setParties(response.data.parties || []);
      setFilteredParties(response.data.parties || []);
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const getRoleFilterLabel = () => {
    const map = {
      vendor: 'Vendors',
      customer: 'Customers',
      tailor: 'Tailors',
      shop_expense: 'Shop Expense'
    };
    return map[roleFilter] || 'All Roles';
  };

  const handleDownloadPartiesPdf = async () => {
    if (partiesPdfLoading) return;
    const rows = filteredParties || [];
    if (!rows.length) {
      error('No parties found for the selected filters.');
      return;
    }
    setPartiesPdfLoading(true);
    try {
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const timeStr = now.toLocaleTimeString();
      const roleLabel = getRoleFilterLabel();
      const branchName = currentBranch?.name || 'All Branches';

      let totalToPay = 0;
      let totalToReceive = 0;
      const body = rows.map((p) => {
        const bal = parseFloat(p.balance) || 0;
        const toPay = bal < 0 ? Math.abs(bal) : 0;
        const toReceive = bal > 0 ? bal : 0;
        totalToPay += toPay;
        totalToReceive += toReceive;
        return [
          p.name || '—',
          p.city || '—',
          p.phone || '—',
          toPay > 0 ? formatPKR(toPay) : '—',
          toReceive > 0 ? formatPKR(toReceive) : '—'
        ];
      });

      body.push([
        'Total',
        '',
        '',
        totalToPay > 0 ? formatPKR(totalToPay) : formatPKR(0),
        totalToReceive > 0 ? formatPKR(totalToReceive) : formatPKR(0)
      ]);

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const { nextY } = await addPdfBrandLogo(doc, { maxWidthMm: 36, startY: 10 });
      let y = nextY;
      doc.setFontSize(17);
      doc.setTextColor(17, 24, 39);
      doc.text(PDF_BUSINESS_NAME, 14, y);
      y += 7;
      doc.setFontSize(16);
      doc.setTextColor(34, 34, 34);
      doc.text('Parties Outstanding Record', 14, y);
      y += 6;
      doc.setFontSize(10);
      doc.setTextColor(90, 90, 90);
      doc.text(`Business: ${PDF_BUSINESS_NAME}`, 14, y);
      y += 5;
      doc.text(`Branch: ${branchName}`, 14, y);
      y += 5;
      doc.text(`Type: ${roleLabel}`, 14, y);
      y += 5;
      doc.text(`Record till: ${dateStr} ${timeStr}`, 14, y);
      y += 5;
      doc.text('To Pay = you owe | To Receive = they owe you', 14, y);
      y += 5;
      doc.setFontSize(10);
      doc.setTextColor(55, 65, 81);
      doc.text(
        `Totals — To Pay: ${formatPKR(totalToPay)} | To Receive: ${formatPKR(totalToReceive)}`,
        14,
        y
      );
      y += 5;

      autoTable(doc, {
        startY: y + 2,
        head: [['Name', 'City', 'Phone', 'To Pay', 'To Receive']],
        body,
        styles: { fontSize: 9, cellPadding: 2.8, valign: 'middle' },
        headStyles: { fillColor: [245, 245, 245], textColor: [51, 51, 51] },
        tableLineColor: [221, 221, 221],
        tableLineWidth: 0.1,
        didParseCell: (data) => {
          const isTotalRow = data.section === 'body' && data.row.index === body.length - 1;
          if (isTotalRow) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [250, 250, 250];
          }
        }
      });

      const roleSlug = roleFilter || 'all';
      doc.save(`parties-${roleSlug}-${dateStr}.pdf`);
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setPartiesPdfLoading(false);
    }
  };

  const LEDGER_PAGE_SIZE = 50;

  const fetchLedger = async (partyId, append = false) => {
    if (!partyId) return;
    const offset = append ? ledgerEntries.length : 0;
    if (append) setLedgerLoadingMore(true);
    else setLedgerLoading(true);
    try {
      const response = await axios.get(`${API}/parties/${partyId}/ledger`, { params: { limit: LEDGER_PAGE_SIZE, offset } });
      const list = response.data.ledger || [];
      if (append) {
        setLedgerEntries(prev => [...prev, ...list]);
      } else {
        setLedgerEntries(list);
        if (response.data.party) {
          setSelectedParty(prev => (prev && response.data.party ? { ...prev, ...response.data.party } : prev));
        }
      }
      setLedgerTotal(response.data.total ?? list.length);
      setLedgerHasMore(!!response.data.has_more);
    } catch (err) {
      if (!append) setLedgerEntries([]);
      error(getErrorMessage(err));
    } finally {
      if (append) setLedgerLoadingMore(false);
      else setLedgerLoading(false);
    }
  };

  const fetchPartyCheques = async (partyId, append = false) => {
    if (!partyId) return;
    const offset = append ? partyCheques.length : 0;
    if (append) setPartyChequesLoadingMore(true);
    else setPartyChequesLoading(true);
    try {
      const res = await axios.get(`${API}/parties/${partyId}/cheques`, { params: { limit: LEDGER_PAGE_SIZE, offset } });
      const list = res.data.cheques || [];
      if (append) {
        setPartyCheques(prev => [...prev, ...list]);
      } else {
        setPartyCheques(list);
      }
      setPartyChequesTotal(res.data.total ?? list.length);
      setPartyChequesHasMore(!!res.data.has_more);
    } catch (err) {
      if (!append) setPartyCheques([]);
    } finally {
      if (append) setPartyChequesLoadingMore(false);
      else setPartyChequesLoading(false);
    }
  };

  const handleViewLedger = async (party) => {
    setSelectedParty(party);
    setLedgerEntries([]);
    setLedgerTotal(0);
    setLedgerHasMore(false);
    setPartyCheques([]);
    setPartyChequesTotal(0);
    setPartyChequesHasMore(false);
    await fetchLedger(party.id);
    fetchPartyCheques(party.id);
    setShowLedgerModal(true);
  };

  const handleReverseLedgerEntry = async (entry) => {
    if (!selectedParty?.id || !entry?.id) return;
    const meta = entry?.meta || {};
    const isReversal = entry?.reference_type === 'ledger_reversal'
      || meta?.isReversal === true || meta?.is_reversal === true
      || meta?.reversalOfLedgerId || meta?.reversal_of_ledger_id;
    if (isReversal) {
      error('Reversal entries cannot be reversed again.');
      return;
    }
    if (meta?.reversedAt || meta?.reversed_at || meta?.reversedByLedgerId || meta?.reversed_by_ledger_id) {
      error('This ledger entry is already reversed.');
      return;
    }
    const reason = window.prompt('Reason for reversal (required for audit):');
    if (!reason || !String(reason).trim()) return;
    setReversingLedgerId(entry.id);
    try {
      await axios.post(`${API}/parties/${selectedParty.id}/ledger/${entry.id}/reverse`, {
        reason: String(reason).trim(),
        transactionDate: new Date().toISOString().split('T')[0]
      });
      success('Ledger entry reversed successfully.');
      await fetchLedger(selectedParty.id, false);
      fetchPartyCheques(selectedParty.id, false);
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setReversingLedgerId(null);
    }
  };

  const fetchPartyAuditHistory = async (party) => {
    const partyId = (party && (party.id ?? party.party_id)) || null;
    if (!partyId) return;
    const businessId = user?.businessId || user?.business_id;
    if (!businessId) return;
    setPartyAuditLoading(true);
    try {
      const res = await axios.get(`${API}/activity-logs/business/${businessId}`, {
        params: {
          entityType: 'party',
          entityId: partyId,
          limit: 200,
          offset: 0
        }
      });
      setPartyAuditEntries(res.data.activities || []);
    } catch (err) {
      setPartyAuditEntries([]);
      error(getErrorMessage(err));
    } finally {
      setPartyAuditLoading(false);
    }
  };

  const fetchFullStatement = async (partyId) => {
    const from = '2000-01-01';
    const to = new Date().toISOString().split('T')[0];
    const res = await axios.get(`${API}/parties/${partyId}/statement`, { params: { from, to } });
    return res.data;
  };

  const getPartyId = (party) => (party && (party.id ?? party.party_id)) || null;

  const handlePrintStatement = async (partyArg = null, dataArg = null) => {
    const party = partyArg || selectedParty;
    if (!party) return;
    const partyId = getPartyId(party);
    if (!partyId) {
      error('Party not found. Please open the statement from the party row again.');
      return;
    }
    setStatementPdfLoading(true);
    try {
      const data = dataArg || await fetchFullStatement(partyId);
      const p = data.party || party;
      const ledger = data.ledger || [];
      const headerCity = (p?.city != null && String(p.city).trim() !== '') ? String(p.city).trim() : '—';
      const headerPhone = (p?.phone != null && String(p.phone).trim() !== '') ? String(p.phone).trim() : '—';
      const win = window.open('', '_blank');
      if (!win) {
        error('Please allow popups to print the statement.');
        return;
      }
      const esc = (s) => (s ?? '').toString().replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const isInvoiceType = (entry) => entry.reference_type === 'sale' || entry.reference_type === 'purchase' || entry.transaction_type === 'opening_balance';
      const paymentsAfterByIndex = {};
      (ledger || []).forEach((entry, i) => {
        if (!isInvoiceType(entry)) return;
        const payments = [];
        for (let j = i + 1; j < (ledger || []).length; j++) {
          if (isInvoiceType(ledger[j])) break;
          const e = ledger[j];
          if (e.transaction_type !== 'payment_in' && e.transaction_type !== 'payment_out') continue;
          const meta = e.meta || {};
          const splits = meta.paymentSplits ?? meta.payment_splits;
          const cr = parseFloat(e.credit) || 0;
          const dr = parseFloat(e.debit) || 0;
          const amt = cr > 0 ? cr : dr;
          if (Array.isArray(splits) && splits.length) {
            splits.forEach((s) => {
              const a = parseFloat(s.amount) || 0;
              if (a <= 0) return;
              const pm = s.paymentMethod ?? s.payment_method;
              const cn = s.chequeNumber ?? s.cheque_number;
              payments.push({ amount: a, method: pm === 'cheque' ? (cn ? `Cheque #${cn}` : 'Cheque') : (pm || '—') });
            });
          } else {
            const pm = meta.paymentMethod ?? meta.payment_method;
            const cn = meta.chequeNumber ?? meta.cheque_number;
            payments.push({ amount: amt, method: pm === 'cheque' ? (cn ? `Cheque #${cn}` : 'Cheque') : (pm || '—') });
          }
        }
        paymentsAfterByIndex[i] = payments;
      });
      let tbodyHtml = '';
      (ledger || []).forEach((e, index) => {
        const dr = parseFloat(e.debit) || 0;
        const cr = parseFloat(e.credit) || 0;
        const balStr = p?.is_shop_expense ? '—' : formatBalanceText(e.balance_after ?? e.running_balance, p?.is_shop_expense);
        let desc = e.description || e.entry_type_label || e.reference_type || '—';
        if (e.sale_details || e.purchase_details) {
          const payList = paymentsAfterByIndex[index];
          if (payList?.length) desc += '\nPayments against this invoice: ' + payList.map((p) => `${formatPKR(p.amount)} (${p.method})`).join(', ');
          else desc += '\n—';
        }
        const typeLabel = getStatementTypeLabel(e);
        const billRef = getStatementBillRef(e);
        const meta = e.meta || {};
        const paymentStr = getStatementPaymentMethodText(meta);
        tbodyHtml += `<tr>
          <td>${esc(formatDate(e.transaction_date))}</td>
          <td>${esc(typeLabel)}</td>
          <td>${esc(billRef)}</td>
          <td>${esc(desc)}</td>
          <td class="num">${dr > 0 ? esc(formatPKR(dr)) : '—'}</td>
          <td class="num">${cr > 0 ? esc(formatPKR(cr)) : '—'}</td>
          <td>${esc(paymentStr)}</td>
          <td class="num">${esc(balStr)}</td>
        </tr>`;
        if (e.sale_details?.items?.length) {
          const saleBillRef = `Sale #${e.sale_details.invoice_number || e.reference_id || ''} – Line items`;
          tbodyHtml += `<tr class="detail-row"><td colspan="8"><div class="bill-caption">${esc(saleBillRef)}</div><table class="nested"><thead><tr><th>Product</th><th>Code</th><th>Variant</th><th>Qty</th><th>Unit</th><th>Amount</th></tr></thead><tbody>`;
          e.sale_details.items.forEach((item) => {
            tbodyHtml += `<tr><td>${esc(item.product_name)}</td><td>${esc(item.product_code || '—')}</td><td>${esc(item.variant_name || item.variant_sku || '—')}</td><td>${item.quantity}</td><td class="num">${esc(formatPKR(item.unit_price))}</td><td class="num">${esc(formatPKR(item.total_price))}</td></tr>`;
          });
          tbodyHtml += `</tbody><tfoot><tr><td colspan="5">Sale total</td><td class="num">${esc(formatPKR(e.sale_details.total_amount))}</td></tr></tfoot></table></td></tr>`;
        }
        if (e.purchase_details?.items?.length) {
          const purchaseBillRef = `Purchase #${e.purchase_details.purchase_number || e.reference_id || ''} – Line items`;
          tbodyHtml += `<tr class="detail-row"><td colspan="8"><div class="bill-caption">${esc(purchaseBillRef)}</div><table class="nested"><thead><tr><th>Product</th><th>Code</th><th>Variant</th><th>Qty</th><th>Unit</th><th>Amount</th></tr></thead><tbody>`;
          e.purchase_details.items.forEach((item) => {
            tbodyHtml += `<tr><td>${esc(item.product_name)}</td><td>${esc(item.product_code || '—')}</td><td>${esc(item.variant_name || item.variant_sku || '—')}</td><td>${item.quantity}</td><td class="num">${esc(formatPKR(item.unit_price))}</td><td class="num">${esc(formatPKR(item.total_price))}</td></tr>`;
          });
          tbodyHtml += `</tbody><tfoot><tr><td colspan="5">Purchase total</td><td class="num">${esc(formatPKR(e.purchase_details.total_amount))}</td></tr></tfoot></table></td></tr>`;
        }
      });
      const currentBalanceStr = formatBalanceText(p?.balance, p?.is_shop_expense);
      win.document.write(`
        <!DOCTYPE html><html><head><title>Statement - ${esc(p.name || '')}</title>
        <style>
          @page {
            size: A4 landscape;
            margin: 10mm;
          }
          html, body {
            width: 100%;
            max-width: 100%;
            margin: 0;
            padding: 0;
          }
          body { font-family: sans-serif, system-ui, Arial; padding: 12px 16px; }
          h1 { font-size: 18px; margin: 0 0 6px 0; }
          .party-meta { color: #333; font-size: 12px; margin: 0 0 4px 0; }
          .period { color: #666; font-size: 12px; margin: 0 0 10px 0; }
          * { box-sizing: border-box; }
          .statement-table {
            border-collapse: collapse;
            width: 100%;
            max-width: 100%;
            table-layout: fixed;
            font-size: 10px;
            margin: 0 0 12px 0;
            border: 1px solid #6b7280;
          }
          .statement-table th, .statement-table td {
            border: 1px solid #9ca3af;
            padding: 5px 6px;
            text-align: left;
            vertical-align: top;
            word-wrap: break-word;
            overflow-wrap: anywhere;
            word-break: break-word;
            hyphens: auto;
            min-width: 0;
          }
          .statement-table thead th {
            background: #374151 !important;
            color: #ffffff !important;
            font-weight: 700;
            font-size: 9px;
            line-height: 1.25;
            white-space: normal;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          /* Amount columns: right align; values stay on one line */
          .statement-table thead th:nth-child(5),
          .statement-table thead th:nth-child(6),
          .statement-table thead th:nth-child(8) {
            text-align: right;
            white-space: normal;
            line-height: 1.2;
          }
          .statement-table tbody td:nth-child(5),
          .statement-table tbody td:nth-child(6),
          .statement-table tbody td:nth-child(8) {
            text-align: right;
            white-space: nowrap;
            font-variant-numeric: tabular-nums;
          }
          .statement-table tbody td:nth-child(1) { white-space: nowrap; }
          .statement-table tbody td:nth-child(2),
          .statement-table tbody td:nth-child(3),
          .statement-table tbody td:nth-child(4),
          .statement-table tbody td:nth-child(7) {
            white-space: normal;
          }
          .statement-table .num { text-align: right; }
          .statement-table .detail-row td { padding: 4px; background: #fafafa; white-space: normal; }
          .statement-table .nested { width: 100%; font-size: 9px; border-collapse: collapse; margin: 0; table-layout: fixed; }
          .statement-table .nested th, .statement-table .nested td { border: 1px solid #d1d5db; padding: 4px 5px; word-break: break-word; white-space: normal; }
          @media print {
            @page { size: A4 landscape; margin: 10mm; }
            body {
              margin: 0;
              padding: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .statement-table {
              width: 100% !important;
              max-width: 100% !important;
              font-size: 8.5pt;
            }
            .statement-table thead th { font-size: 8pt; }
            thead { display: table-header-group; }
            tr { page-break-inside: avoid; }
          }
          .statement-table .nested thead th {
            background: #4b5563 !important;
            color: #ffffff !important;
            font-weight: 600;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .statement-table .bill-caption { font-weight: 600; margin-bottom: 4px; font-size: 10px; color: #374151; }
          .statement-footer { margin-top: 12px; font-weight: bold; }
        </style></head><body>
        <h1>Statement – ${esc(p.name || '')}</h1>
        <p class="party-meta">City: ${esc(headerCity)} · Phone: ${esc(headerPhone)}</p>
        <p class="period">Period: ${esc(data.statement_from || '')} to ${esc(data.statement_to || '')}</p>
        <table class="statement-table">
          <colgroup>
            <col style="width:8%" /><col style="width:9%" /><col style="width:14%" /><col style="width:26%" />
            <col style="width:11%" /><col style="width:11%" /><col style="width:10%" /><col style="width:11%" />
          </colgroup>
          <thead><tr><th>Date</th><th>Type</th><th>Bill / Reference</th><th>Description</th><th>Amount (Dr)</th><th>Amount paid (Cr)</th><th>Payment / Cheque</th><th>Balance</th></tr></thead>
          <tbody>${tbodyHtml}</tbody>
        </table>
        <div class="statement-footer">Current balance: ${esc(currentBalanceStr)}</div>
        </body></html>
      `);
      win.document.close();
      win.focus();
      setTimeout(() => { win.print(); win.close(); }, 300);
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setStatementPdfLoading(false);
    }
  };

  const handleDownloadPdfStatement = async (partyArg = null, dataArg = null) => {
    const party = partyArg || selectedParty;
    if (!party) return;
    const partyId = getPartyId(party);
    if (!partyId) {
      error('Party not found. Please open the statement from the party row again.');
      return;
    }
    setStatementPdfLoading(true);
    try {
      const data = dataArg || await fetchFullStatement(partyId);
      const p = data.party || party;
      const ledger = data.ledger || [];
      const headerCityPdf = (p?.city != null && String(p.city).trim() !== '') ? String(p.city).trim() : '—';
      const headerPhonePdf = (p?.phone != null && String(p.phone).trim() !== '') ? String(p.phone).trim() : '—';
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pdfMargin = 12;
      const statementTableWidth = pageW - 2 * pdfMargin;
      const colFrac = [0.09, 0.10, 0.16, 0.26, 0.09, 0.09, 0.10, 0.11];
      const colW = colFrac.map((f) => Math.floor(statementTableWidth * f * 100) / 100);
      const widthDrift = statementTableWidth - colW.reduce((a, b) => a + b, 0);
      colW[7] = Math.round((colW[7] + widthDrift) * 100) / 100;
      const { nextY: statementHeaderY } = await addPdfBrandLogo(doc, { maxWidthMm: 28, startY: 6, gapAfterMm: 3 });
      let stmtY = statementHeaderY;
      doc.setFontSize(15);
      doc.setTextColor(17, 24, 39);
      doc.text(PDF_BUSINESS_NAME, pdfMargin, stmtY);
      stmtY += 6;
      doc.setFontSize(13);
      doc.setTextColor(0, 0, 0);
      doc.text(`Statement – ${p.name || 'Party'}`, pdfMargin, stmtY);
      stmtY += 5;
      doc.setFontSize(9);
      doc.setTextColor(51, 51, 51);
      doc.text(`City: ${headerCityPdf} · Phone: ${headerPhonePdf}`, pdfMargin, stmtY);
      stmtY += 5;
      doc.setTextColor(102, 102, 102); /* #666 - match print period color */
      doc.text(`Period: ${data.statement_from || ''} to ${data.statement_to || ''}`, pdfMargin, stmtY);
      stmtY += 5;
      doc.setTextColor(0, 0, 0);
      const statementTableStartY = stmtY + 2;
      const isInvoiceTypePdf = (entry) => entry.reference_type === 'sale' || entry.reference_type === 'purchase' || entry.transaction_type === 'opening_balance';
      const paymentsAfterByIndexPdf = {};
      (ledger || []).forEach((entry, i) => {
        if (!isInvoiceTypePdf(entry)) return;
        const payments = [];
        for (let j = i + 1; j < (ledger || []).length; j++) {
          if (isInvoiceTypePdf(ledger[j])) break;
          const e = ledger[j];
          if (e.transaction_type !== 'payment_in' && e.transaction_type !== 'payment_out') continue;
          const meta = e.meta || {};
          const splits = meta.paymentSplits ?? meta.payment_splits;
          const cr = parseFloat(e.credit) || 0;
          const dr = parseFloat(e.debit) || 0;
          const amt = cr > 0 ? cr : dr;
          if (Array.isArray(splits) && splits.length) {
            splits.forEach((s) => {
              const a = parseFloat(s.amount) || 0;
              if (a <= 0) return;
              const pm = s.paymentMethod ?? s.payment_method;
              const cn = s.chequeNumber ?? s.cheque_number;
              payments.push({ amount: a, method: pm === 'cheque' ? (cn ? `Cheque #${cn}` : 'Cheque') : (pm || '—') });
            });
          } else {
            const pm = meta.paymentMethod ?? meta.payment_method;
            const cn = meta.chequeNumber ?? meta.cheque_number;
            payments.push({ amount: amt, method: pm === 'cheque' ? (cn ? `Cheque #${cn}` : 'Cheque') : (pm || '—') });
          }
        }
        paymentsAfterByIndexPdf[i] = payments;
      });
      const bodyRows = [];
      (ledger || []).forEach((e, index) => {
        const dr = parseFloat(e.debit) || 0;
        const cr = parseFloat(e.credit) || 0;
        const balStr = p?.is_shop_expense ? '—' : formatBalanceText(e.balance_after ?? e.running_balance, p?.is_shop_expense);
        let desc = e.description || e.entry_type_label || e.reference_type || '—';
        if (e.sale_details || e.purchase_details) {
          const payList = paymentsAfterByIndexPdf[index];
          if (payList?.length) desc += ' | Payments: ' + payList.map((p) => `${formatPKR(p.amount)} (${p.method})`).join(', ');
          else desc += ' | —';
        }
        const meta = e.meta || {};
        const paymentMethodPdf = getStatementPaymentMethodText(meta);
        const paymentStr = paymentMethodPdf.length > 48
          ? `${paymentMethodPdf.slice(0, 45)}…`
          : paymentMethodPdf;
        bodyRows.push([
          formatDate(e.transaction_date),
          getStatementTypeLabel(e).substring(0, 14),
          getStatementBillRef(e),
          desc,
          dr > 0 ? formatPKR(dr) : '—',
          cr > 0 ? formatPKR(cr) : '—',
          paymentStr,
          balStr
        ]);
        if (e.sale_details?.items?.length) {
          e.sale_details.items.forEach((item) => {
            const productLabel = (item.product_name || '') + (item.product_code ? ` (${item.product_code})` : '');
            bodyRows.push(['', '', `  ${productLabel.substring(0, 22)}`, (item.variant_name || item.variant_sku || '—').substring(0, 20), '', formatPKR(item.total_price), '', '']);
          });
          bodyRows.push(['', '', '', 'Sale total', '', formatPKR(e.sale_details.total_amount), '', '']);
        }
        if (e.purchase_details?.items?.length) {
          e.purchase_details.items.forEach((item) => {
            const productLabel = (item.product_name || '') + (item.product_code ? ` (${item.product_code})` : '');
            bodyRows.push(['', '', `  ${productLabel.substring(0, 22)}`, (item.variant_name || item.variant_sku || '—').substring(0, 20), '', formatPKR(item.total_price), '', '']);
          });
          bodyRows.push(['', '', '', 'Purchase total', '', formatPKR(e.purchase_details.total_amount), '', '']);
        }
      });
      autoTable(doc, {
        head: [['Date', 'Type', 'Bill / Ref', 'Description', 'Dr', 'Cr', 'Payment', 'Balance']],
        body: bodyRows,
        startY: statementTableStartY,
        margin: { left: pdfMargin, right: pdfMargin },
        tableWidth: statementTableWidth,
        styles: {
          fontSize: 5,
          textColor: [15, 23, 42],
          lineColor: [107, 114, 128],
          lineWidth: 0.15,
          cellPadding: 1.5,
          valign: 'top'
        },
        headStyles: {
          fillColor: [55, 65, 81],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 6,
          lineColor: [75, 85, 99],
          lineWidth: 0.15
        },
        theme: 'plain',
        didParseCell: (data) => {
          const row = bodyRows[data.row.index];
          if (data.section === 'body' && row && row[0] === '' && row[1] === '') {
            data.cell.styles.fillColor = [250, 250, 250]; /* #fafafa - match print detail-row background */
          }
        },
        columnStyles: {
          0: { cellWidth: colW[0], halign: 'left' },
          1: { cellWidth: colW[1], halign: 'left' },
          2: { cellWidth: colW[2], halign: 'left' },
          3: { cellWidth: colW[3], halign: 'left', overflow: 'linebreak' },
          4: { halign: 'right', cellWidth: colW[4] },
          5: { halign: 'right', cellWidth: colW[5] },
          6: { cellWidth: colW[6], halign: 'left' },
          7: { halign: 'right', cellWidth: colW[7] }
        }
      });
      const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY || 24 : 24;
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text(`Current balance: ${formatBalanceText(p?.balance, p?.is_shop_expense)}`, pdfMargin, finalY + 8);
      const safeName = (p.name || 'party').replace(/[^a-z0-9-_]/gi, '_').substring(0, 40);
      doc.save(`statement_${safeName}_${data.statement_to || 'statement'}.pdf`);
      success('PDF downloaded.');
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setStatementPdfLoading(false);
    }
  };

  const sortedPartyCheques = useMemo(() => {
    const list = [...(partyCheques || [])];
    const key = partyChequesSortBy;
    const order = partyChequesSortOrder === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      let va, vb;
      if (key === 'date') {
        va = (a.cheque_date || a.created_at) ? new Date(a.cheque_date || a.created_at).getTime() : 0;
        vb = (b.cheque_date || b.created_at) ? new Date(b.cheque_date || b.created_at).getTime() : 0;
      } else if (key === 'amount') {
        va = parseFloat(a.amount) || 0;
        vb = parseFloat(b.amount) || 0;
      } else if (key === 'status') {
        const statusOrder = { deleted: 0, pending: 1, upcoming: 2, cashed: 3 };
        va = statusOrder[(a.display_status || a.status || '').toLowerCase()] ?? -1;
        vb = statusOrder[(b.display_status || b.status || '').toLowerCase()] ?? -1;
      } else if (key === 'cashed_date') {
        va = (a.approved_at && new Date(a.approved_at).getTime()) || 0;
        vb = (b.approved_at && new Date(b.approved_at).getTime()) || 0;
      } else {
        va = a[key] != null ? new Date(a[key]).getTime() || 0 : 0;
        vb = b[key] != null ? new Date(b[key]).getTime() || 0 : 0;
      }
      if (va < vb) return -order;
      if (va > vb) return order;
      return 0;
    });
    return list;
  }, [partyCheques, partyChequesSortBy, partyChequesSortOrder]);

  const handleTurnIntoCash = (cheque) => {
    setChequeToApprove(cheque);
    setApproveSplits([{ accountId: '', amount: String(cheque.amount || '') }]);
  };

  useEffect(() => {
    if (!chequeToApprove) return;
    setApproveSplits([{ accountId: '', amount: String(chequeToApprove.amount || '') }]);
  }, [chequeToApprove?.id]);

  const handleApproveChequeFromLedger = async (e) => {
    e?.preventDefault();
    if (!chequeToApprove) return;
    const isGiven = (chequeToApprove.payment_type || chequeToApprove.paymentType) === 'payment_out';
    const chequeAmount = parseFloat(chequeToApprove.amount) || 0;
    if (isGiven) {
      setApproveSubmitting(true);
      try {
        await axios.post(`${API}/funds/pending-cheques/${chequeToApprove.id}/approve`, {});
        success('Cheque cleared. Bank account has been debited.');
        setChequeToApprove(null);
        if (selectedParty) {
          fetchLedger(selectedParty.id);
          fetchPartyCheques(selectedParty.id);
        }
      } catch (err) {
        error(getErrorMessage(err));
      } finally {
        setApproveSubmitting(false);
      }
      return;
    }
    const splits = approveSplits.filter(s => s.accountId && (parseFloat(s.amount) || 0) > 0);
    const sum = splits.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
    if (Math.abs(sum - chequeAmount) > 0.01) {
      error(`Total (${sum.toFixed(2)}) must equal cheque amount (${chequeAmount.toFixed(2)})`);
      return;
    }
    if (splits.length === 0) {
      error('Add at least one account and amount.');
      return;
    }
    setApproveSubmitting(true);
    try {
      const payload = splits.length === 1 && Math.abs((parseFloat(splits[0].amount) || 0) - chequeAmount) < 0.01
        ? { accountId: parseInt(splits[0].accountId, 10) }
        : { splits: splits.map(s => ({ accountId: parseInt(s.accountId, 10), amount: parseFloat(s.amount) })) };
      await axios.post(`${API}/funds/pending-cheques/${chequeToApprove.id}/approve`, payload);
      success('Cheque cleared. Amount deposited to selected account(s).');
      setChequeToApprove(null);
      if (selectedParty) {
        fetchLedger(selectedParty.id);
        fetchPartyCheques(selectedParty.id);
      }
    } catch (err) {
      error(getErrorMessage(err));
} finally {
        setApproveSubmitting(false);
      }
  };

  const handleDeletePartyCheque = async (cheque) => {
    if (!cheque?.id || !selectedParty) return;
    const status = (cheque.display_status || cheque.status || '').toLowerCase();
    if (status === 'cashed' || status === 'deleted') {
      error('Only pending or upcoming cheques can be deleted.');
      return;
    }
    if (!window.confirm('Delete this cheque? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/funds/pending-cheques/${cheque.id}`);
      success('Cheque deleted.');
      fetchPartyCheques(selectedParty.id);
      fetchLedger(selectedParty.id);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const openStatement = (party) => {
    if (!party || (party.id == null && party.party_id == null)) {
      error('Party not found.');
      return;
    }
    setStatementParty(party);
    const d = new Date();
    const from = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
    const to = d.toISOString().split('T')[0];
    setStatementFrom(from);
    setStatementTo(to);
    setStatementData(null);
    setShowStatementModal(true);
    fetchStatement(getPartyId(party), from, to);
  };

  const fetchStatement = (partyId, from, to) => {
    if (!partyId) return;
    setStatementLoading(true);
    const fromParam = from || statementFrom;
    const toParam = to || statementTo;
    axios.get(`${API}/parties/${partyId}/statement?from=${fromParam}&to=${toParam}`)
      .then(res => {
        setStatementData(res.data);
        if (res.data.statement_from) setStatementFrom(res.data.statement_from);
        if (res.data.statement_to) setStatementTo(res.data.statement_to);
      })
      .catch(err => {
        error(getErrorMessage(err));
        setStatementData(null);
      })
      .finally(() => setStatementLoading(false));
  };

  const handleStatementApply = () => {
    if (statementParty) fetchStatement(getPartyId(statementParty), statementFrom, statementTo);
  };

  const handleAllProductsAvailable = async () => {
    if (!selectedParty || !(selectedParty.is_vendor || selectedParty.isVendor)) return;
    const bid = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
    if (!bid) {
      error('Business context required');
      return;
    }
    setLoadingVendorPurchases(true);
    setShowReturnFormModal(true);
    setReturnQtyErrors({});
    setReturnFormData({ returnDate: new Date().toISOString().split('T')[0], items: [], reason: '', notes: '' });
    try {
      const params = new URLSearchParams();
      if (effectiveBranchId) params.set('branchId', effectiveBranchId);
      params.set('partyId', selectedParty.id);
      params.set('status', 'confirmed');
      const res = await axios.get(`${API}/purchases/business/${bid}?${params}`);
      const list = res.data.purchases || [];
      const items = [];
      list.forEach((purchase) => {
        const purchaseNumber = purchase.purchase_number || purchase.id;
        (purchase.items || []).forEach((it) => {
          const variant = it.variant;
          const variantLabel = variant
            ? [variant.color, variant.size].filter(Boolean).filter(v => String(v).toLowerCase() !== 'default').join(' / ') || '—'
            : '—';
          const qty = parseFloat(it.quantity) || 0;
          const unitPrice = parseFloat(it.unit_price) || 0;
          items.push({
            purchaseId: purchase.id,
            purchaseNumber,
            purchaseItemId: it.id,
            productCode: (it.product && (it.product.product_code || it.product.productCode)) || '',
            name: (it.product && it.product.name) || '',
            variantLabel,
            max: qty,
            returnQty: '',
            unitPrice: unitPrice,
            total: 0,
            reason: ''
          });
        });
      });
      setReturnFormData({
        returnDate: new Date().toISOString().split('T')[0],
        items,
        reason: '',
        notes: ''
      });
    } catch (err) {
      error(getErrorMessage(err));
      setShowReturnFormModal(false);
    } finally {
      setLoadingVendorPurchases(false);
    }
  };

  const updateReturnItem = (index, field, value) => {
    if (field === 'returnQty') {
      const maxQty = parseFloat(returnFormData.items[index]?.max) || 0;
      const qty = parseFloat(value) || 0;
      const err = (value !== '' && !Number.isNaN(qty) && qty > maxQty) ? `Max is ${maxQty}` : null;
      setReturnQtyErrors((prev) => ({ ...prev, [index]: err }));
    }
    setReturnFormData((prev) => {
      const next = { ...prev, items: [...prev.items] };
      const item = { ...next.items[index], [field]: value };
      if (field === 'returnQty') {
        const qty = parseFloat(value) || 0;
        item.total = qty * (parseFloat(item.unitPrice) || 0);
      } else if (field === 'unitPrice') {
        const u = parseFloat(value) || 0;
        item.unitPrice = u;
        item.total = (parseFloat(item.returnQty) || 0) * u;
      } else if (field === 'total') {
        item.total = parseFloat(value) || 0;
        const q = parseFloat(item.returnQty);
        if (q > 0) item.unitPrice = item.total / q;
      }
      next.items[index] = item;
      return next;
    });
  };

  const addToReturnCart = (item, qty = 1) => {
    const addQty = Math.min(Math.max(1, parseInt(qty, 10) || 1), item.max || 1);
    setReturnCart((prev) => {
      const existing = prev.findIndex((c) => c.purchaseItemId === item.purchaseItemId && c.purchaseId === item.purchaseId);
      if (existing >= 0) {
        const next = [...prev];
        const cur = next[existing];
        const newQty = Math.min((parseFloat(cur.returnQty) || 0) + addQty, cur.max || 0);
        next[existing] = { ...cur, returnQty: newQty, total: newQty * (parseFloat(cur.unitPrice) || 0) };
        return next;
      }
      const unitPrice = parseFloat(item.unitPrice) || 0;
      return [...prev, { ...item, returnQty: addQty, total: addQty * unitPrice }];
    });
    setReturnQtyErrors({});
  };

  const updateReturnCartItem = (cartIndex, field, value) => {
    setReturnCart((prev) => {
      const next = [...prev];
      const item = { ...next[cartIndex], [field]: value };
      if (field === 'returnQty') {
        const qty = parseFloat(value) || 0;
        item.total = qty * (parseFloat(item.unitPrice) || 0);
      } else if (field === 'unitPrice') {
        const u = parseFloat(value) || 0;
        item.unitPrice = u;
        item.total = (parseFloat(item.returnQty) || 0) * u;
      } else if (field === 'total') {
        item.total = parseFloat(value) || 0;
        const q = parseFloat(item.returnQty);
        if (q > 0) item.unitPrice = item.total / q;
      }
      next[cartIndex] = item;
      return next;
    });
    if (field === 'returnQty') {
      const maxQty = returnCart[cartIndex]?.max || 0;
      const qty = parseFloat(value) || 0;
      const err = (value !== '' && !Number.isNaN(qty) && qty > maxQty) ? `Max ${maxQty}` : null;
      setReturnQtyErrors((prev) => ({ ...prev, [cartIndex]: err }));
    }
  };

  const removeFromReturnCart = (cartIndex) => {
    setReturnCart((prev) => prev.filter((_, i) => i !== cartIndex));
    setReturnQtyErrors((prev) => {
      const next = { ...prev };
      delete next[cartIndex];
      return next;
    });
    if (returnCartEditIndex === cartIndex) setReturnCartEditIndex(null);
  };

  const handleReturnFormSubmit = async (e) => {
    e.preventDefault();
    if (returnCart.length === 0) {
      error('Add at least one product to return.');
      return;
    }
    const errs = {};
    returnCart.forEach((it, i) => {
      const q = parseFloat(it.returnQty) || 0;
      if (q > (it.max || 0)) errs[i] = `Max ${it.max}`;
    });
    setReturnQtyErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const bid = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
    if (!bid || !effectiveBranchId || !selectedParty) {
      error('Missing business, branch, or vendor context.');
      return;
    }

    const byPurchase = {};
    returnCart.forEach((it) => {
      const pid = it.purchaseId;
      if (!byPurchase[pid]) byPurchase[pid] = [];
      byPurchase[pid].push(it);
    });

    const basePayload = {
      businessId: user.role === 'super_admin' ? parseInt(bid, 10) : undefined,
      branchId: parseInt(effectiveBranchId, 10),
      vendorId: selectedParty.id,
      returnDate: returnFormData.returnDate,
      reason: (returnFormData.reason || '').trim() || undefined,
      notes: (returnFormData.notes || '').trim() || undefined
    };

    setSubmittingReturn(true);
    try {
      const createdIds = [];
      for (const purchaseId of Object.keys(byPurchase)) {
        const group = byPurchase[purchaseId];
        const payload = {
          ...basePayload,
          purchaseId: parseInt(purchaseId, 10),
          items: group.map((it) => ({
            purchaseItemId: it.purchaseItemId,
            quantity: parseFloat(it.returnQty),
            unitPrice: parseFloat(it.unitPrice) || 0,
            totalPrice: parseFloat(it.total) || 0,
            reason: (it.reason || '').trim() || undefined
          }))
        };
        const res = await axios.post(`${API}/purchase-returns`, payload);
        const created = res.data?.purchase_return;
        if (created?.id) createdIds.push(created.id);
      }
      success(createdIds.length === 1 ? 'Purchase return created.' : `${createdIds.length} purchase returns created.`);
      closePurchaseReturnFlow();
      if (selectedParty) await fetchLedger(selectedParty.id);
      if (createdIds.length > 0) {
        navigate('/purchases', { state: { viewReturns: true, highlightReturnIds: createdIds } });
      }
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setSubmittingReturn(false);
    }
  };

  const closePurchaseReturnFlow = () => {
    setShowPurchaseSelectModal(false);
    setShowReturnFormModal(false);
    setVendorPurchases([]);
    setSelectedPurchaseForReturn(null);
    setReturnFormData({ returnDate: new Date().toISOString().split('T')[0], items: [], reason: '', notes: '' });
    setReturnSearchTerm('');
    setReturnCart([]);
    setReturnCartEditIndex(null);
    setReturnQtyErrors({});
  };

  const hasVendorReturnForm = showReturnFormModal && selectedParty && (selectedParty.is_vendor || selectedParty.isVendor);

  const handleRecordPayment = (party) => {
    setSelectedParty(party);
    // Find default cash account
    const defaultCashAccount = fundAccounts.find(a => a.type === 'cash');
    setPaymentFormData({
      amount: '',
      paymentType: party.is_shop_expense ? 'payment_out' : (parseFloat(party.balance) < 0 ? 'payment_out' : 'payment_in'),
      paymentSplits: [{ 
        paymentMethod: 'cash', 
        accountId: defaultCashAccount ? String(defaultCashAccount.id) : '', 
        amount: '', 
        chequeNumber: '', 
        chequeDate: '', 
        referenceNumber: '' 
      }],
      description: '',
      transactionDate: new Date().toISOString().split('T')[0]
    });
    paymentSubmitInFlightRef.current = false;
    setPaymentSubmitting(false);
    setShowPaymentModal(true);
  };

  const handleDownloadLedgerEntryReceipt = async (entry) => {
    if (!selectedParty || !entry || ledgerReceiptDownloadingId != null) return;
    const debit = parseFloat(entry.debit) || 0;
    const credit = parseFloat(entry.credit) || 0;
    const amount = debit > 0 ? debit : (credit > 0 ? credit : Math.abs((parseFloat(entry.balance_after) || 0)));
    const meta = entry?.meta || {};
    const reference =
      getPaymentReferenceFromMeta(meta) ||
      meta.referenceNumber ||
      meta.reference_number ||
      meta.chequeNumber ||
      meta.cheque_number ||
      (entry.reference_id ? `${entry.reference_type || 'entry'}-${entry.reference_id}` : `ledger-${entry.id}`);
    const receiptType = (entry.transaction_type === 'payment_out' || debit > credit) ? 'payment_made' : 'payment_received';
    const typeLabel = entry.transaction_type === 'balance_adjustment'
      ? 'Balance edited'
      : String(entry.transaction_type || 'ledger_entry').replace(/_/g, ' ');
    const today = new Date().toISOString().split('T')[0];
    const downloadedAt = new Date();
    const downloadedAtLabel = downloadedAt.toLocaleString('en-PK', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
    const balanceAfterEntry = parseFloat(entry.balance_after) || 0;
    const currentBalance = parseFloat(selectedParty?.balance) || 0;
    let currentBalancePerspective = 'Settled';
    if (!selectedParty?.is_shop_expense) {
      if (currentBalance < 0) currentBalancePerspective = 'Safari Club Will Pay you';
      else if (currentBalance > 0) currentBalancePerspective = 'To pay to Safari Club';
    }
    const currentBalanceLabel = `${formatPKR(Math.abs(currentBalance))} (${currentBalancePerspective})`;
    let balancePerspective = 'Settled';
    if (!selectedParty?.is_shop_expense) {
      if (balanceAfterEntry < 0) balancePerspective = 'Safari Club Will Pay you';
      else if (balanceAfterEntry > 0) balancePerspective = 'To pay to Safari Club';
    }
    const balanceAfterRow = `Balance after entry: ${formatPKR(Math.abs(balanceAfterEntry))} (${balancePerspective}) till Today (${today})`;

    setLedgerReceiptDownloadingId(entry.id);
    try {
      let purchaseItemsRow = '';
      if (entry.reference_type === 'purchase' && entry.reference_id) {
        try {
          const res = await axios.get(`${API}/purchases/${entry.reference_id}`);
          const purchase = res.data?.purchase;
          const items = Array.isArray(purchase?.items) ? purchase.items : [];
          if (items.length > 0) {
            purchaseItemsRow = items
              .map((it) => {
                const code = it.product?.product_code || it.product?.productCode || '—';
                const name = it.product?.name || 'Product';
                const variant = [it.variant?.color, it.variant?.size].filter(Boolean).join(' / ');
                const qty = parseFloat(it.quantity) || 0;
                const unitPrice = parseFloat(it.unit_price ?? it.unitPrice) || 0;
                return `${code} ${name}${variant ? ` (${variant})` : ''} Qty:${qty} @ ${formatPKR(unitPrice)}`;
              })
              .join(' ; ');
          }
        } catch (_) {
          // Keep receipt generation resilient even if purchase detail fetch fails
        }
      }

      const notes = [
        `Type: ${typeLabel}`,
        balanceAfterRow,
        purchaseItemsRow ? `Purchase details: ${purchaseItemsRow}` : ''
      ].filter(Boolean).join(' | ');

      await downloadPartyReceivingReceiptPdf({
        businessName: PDF_BUSINESS_NAME,
        branchName: currentBranch?.name || '—',
        partyName: selectedParty.name,
        amount: amount > 0 ? amount : 0,
        receiptType,
        receiptDate: entry.transaction_date || new Date().toISOString().split('T')[0],
        downloadedAtLabel,
        referenceNumber: reference,
        purpose: entry.description || `Ledger entry #${entry.id}`,
        receivedBy: user?.name || user?.fullName || user?.username || '',
        currentBalanceLabel,
        notes
      });
      success('Receipt downloaded.');
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setLedgerReceiptDownloadingId(null);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (paymentSubmitInFlightRef.current) return;
    if (selectedParty?.is_shop_expense && paymentFormData.paymentType === 'payment_out') {
      const desc = (paymentFormData.description || '').trim();
      if (!desc) {
        error('Description is required for shop expense payments (e.g. what the payment was for).');
        return;
      }
    }
    const totalAmount = parseFloat(paymentFormData.amount);
    const splitsSum = paymentFormData.paymentSplits.reduce((sum, split) => sum + (parseFloat(split.amount) || 0), 0);
    if (Math.abs(splitsSum - totalAmount) > 0.01) {
      error(`Payment splits total (${formatPKR(splitsSum)}) must equal payment amount (${formatPKR(totalAmount)})`);
      return;
    }
    // Validate: non-cheque splits must have account; payment_out cheque must have bank (we gave the cheque)
    const isPaymentOut = paymentFormData.paymentType === 'payment_out';
    const missingAccount = paymentFormData.paymentSplits.find(s => {
      if (s.paymentMethod !== 'cheque') return !s.accountId;
      if (isPaymentOut) return !s.accountId; // When we gave cheque, bank is required
      return false;
    });
    if (missingAccount) {
      if (isPaymentOut && missingAccount.paymentMethod === 'cheque') {
        error('When paying by cheque (we gave), select the bank account the cheque is drawn from.');
      } else {
        error('Please select a deposit account for each cash/bank split. For cheque received, you choose the account when clearing the cheque.');
      }
      return;
    }
    paymentSubmitInFlightRef.current = true;
    setPaymentSubmitting(true);
    try {
      const payload = {
        amount: totalAmount,
        paymentType: paymentFormData.paymentType,
        paymentSplits: paymentFormData.paymentSplits.map(split => ({
          paymentMethod: split.paymentMethod,
          accountId: split.paymentMethod === 'cheque' ? (isPaymentOut ? (parseInt(split.accountId) || null) : null) : (parseInt(split.accountId) || null),
          amount: parseFloat(split.amount),
          chequeNumber: split.paymentMethod === 'cheque' ? (split.chequeNumber || null) : null,
          chequeDate: split.paymentMethod === 'cheque' && split.chequeDate ? split.chequeDate : null,
          referenceNumber: split.paymentMethod !== 'cheque' ? (split.referenceNumber || null) : null
        })),
        description: paymentFormData.description || null,
        transactionDate: paymentFormData.transactionDate
      };
      await axios.post(`${API}/parties/${selectedParty.id}/payment`, payload);
      success(`Payment recorded successfully! Balance updated.`);
      setShowPaymentModal(false);
      // Refresh ledger, party cheques, and parties list
      if (showLedgerModal) {
        await fetchLedger(selectedParty.id);
        fetchPartyCheques(selectedParty.id);
      }
      fetchParties();
    } catch (err) {
      error(getErrorMessage(err));
      paymentSubmitInFlightRef.current = false;
      setPaymentSubmitting(false);
    }
  };

  const handleEdit = (party) => {
    setEditingParty(party);
    const bal = parseFloat(party.balance) || 0;
    // Shop expense: we only pay out; never show "they owe you"
    const openingBalanceDirection = party.is_shop_expense
      ? (bal < 0 ? 'you_owe_them' : '')
      : (bal > 0 ? 'they_owe_you' : bal < 0 ? 'you_owe_them' : '');
    const openingBalanceAmount = (party.is_shop_expense && bal > 0) ? '' : (bal !== 0 ? String(Math.abs(bal)) : '');
    setFormData({
      name: party.name,
      phone: party.phone || '',
      city: party.city || '',
      address: party.address || '',
      bankAccount: party.bank_account || '',
      bankName: party.bank_name || '',
      isVendor: Boolean(party.is_vendor),
      isCustomer: Boolean(party.is_customer),
      isTailor: Boolean(party.is_tailor),
      isAdditional: Boolean(party.is_additional),
      isEmployee: Boolean(party.is_employee),
      isShopExpense: Boolean(party.is_shop_expense),
      salary: party.salary != null ? String(party.salary) : '',
      notes: party.notes || '',
      openingBalanceDirection,
      openingBalanceAmount,
      balanceEditReason: '',
      employeeAdvance: party.meta?.employeeAdvance != null ? String(party.meta.employeeAdvance) : (party.meta?.employee_advance != null ? String(party.meta.employee_advance) : ''),
      salaryPaidForCurrentMonth: Boolean(party.meta?.salaryPaidForPeriod === new Date().toISOString().slice(0, 7) || party.meta?.salary_paid_for_period === new Date().toISOString().slice(0, 7))
    });
    setShowModal(true);
  };

  const handlePaySalary = (party) => {
    setSelectedParty(party);
    const now = new Date();
    setSalaryFormData({
      amount: party.salary ? String(party.salary) : '',
      periodMonth: String(now.getMonth() + 1),
      periodYear: String(now.getFullYear()),
      description: '',
      paymentMethod: 'cash',
      transactionDate: now.toISOString().split('T')[0]
    });
    setShowSalaryModal(true);
  };

  const handleRecordAdvance = (party) => {
    setSelectedParty(party);
    setAdvanceFormData({
      amount: '',
      description: '',
      paymentMethod: 'cash',
      transactionDate: new Date().toISOString().split('T')[0]
    });
    setShowAdvanceModal(true);
  };

  const handleSalarySubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        amount: parseFloat(salaryFormData.amount),
        paymentMethod: salaryFormData.paymentMethod,
        transactionDate: salaryFormData.transactionDate
      };
      if (salaryFormData.accountId) payload.accountId = parseInt(salaryFormData.accountId);
      payload.periodMonth = parseInt(salaryFormData.periodMonth);
      payload.periodYear = parseInt(salaryFormData.periodYear);
      if (salaryFormData.description) payload.description = salaryFormData.description;
      await axios.post(`${API}/parties/${selectedParty.id}/salary`, payload);
      success('Salary payment recorded!');
      setShowSalaryModal(false);
      fetchParties();
      if (showLedgerModal) await fetchLedger(selectedParty.id);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleAdvanceSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        amount: parseFloat(advanceFormData.amount),
        paymentMethod: advanceFormData.paymentMethod,
        transactionDate: advanceFormData.transactionDate
      };
      if (advanceFormData.accountId) payload.accountId = parseInt(advanceFormData.accountId);
      if (advanceFormData.description) payload.description = advanceFormData.description;
      await axios.post(`${API}/parties/${selectedParty.id}/advance`, payload);
      success('Advance recorded!');
      setShowAdvanceModal(false);
      fetchParties();
      if (showLedgerModal) await fetchLedger(selectedParty.id);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleDelete = (partyId) => {
    setPartyToDelete(partyId);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/parties/${partyToDelete}`);
      success('Party deleted successfully!');
      setShowConfirmModal(false);
      setPartyToDelete(null);
      fetchParties();
    } catch (err) {
      error(getErrorMessage(err));
      setShowConfirmModal(false);
      setPartyToDelete(null);
    }
  };

  const getUpdateChanges = () => {
    if (!editingParty) return [];
    const p = editingParty;
    const f = formData;
    const changes = [];
    const add = (label, before, after) => {
      const b = before ?? '—';
      const a = after ?? '—';
      if (String(b).trim() !== String(a).trim()) changes.push({ label, before: b, after: a });
    };
    add('Name', p.name, f.name);
    add('Phone', p.phone, f.phone);
    add('City', p.city, f.city);
    add('Address', p.address, f.address);
    add('Bank Name', p.bank_name, f.bankName);
    add('Bank Account', p.bank_account, f.bankAccount);
    add('Notes', p.notes, f.notes);
    const rolesBefore = [p.is_vendor && 'Vendor', p.is_customer && 'Customer', p.is_tailor && 'Tailor', p.is_additional && 'Additional', p.is_shop_expense && 'Shop Expense'].filter(Boolean).join(', ') || 'None';
    const rolesAfter = [f.isVendor && 'Vendor', f.isCustomer && 'Customer', f.isTailor && 'Tailor', f.isAdditional && 'Additional', f.isShopExpense && 'Shop Expense'].filter(Boolean).join(', ') || 'None';
    if (rolesBefore !== rolesAfter) changes.push({ label: 'Roles', before: rolesBefore, after: rolesAfter });
    const amount = parseFloat(f.openingBalanceAmount) || 0;
    const dir = f.openingBalanceDirection;
    const newBal = (dir === 'they_owe_you' ? amount : dir === 'you_owe_them' ? -amount : 0);
    const curBal = parseFloat(p.balance) || 0;
    if (Math.abs(newBal - curBal) >= 0.01) {
      changes.push({ label: 'Opening balance', before: curBal, after: newBal });
      if ((f.balanceEditReason || '').trim()) changes.push({ label: 'Reason for balance edit', before: '—', after: (f.balanceEditReason || '').trim() });
    }
    return changes;
  };

  const isOpeningBalanceChanged = () => {
    if (!editingParty) return false;
    const amount = parseFloat(formData.openingBalanceAmount) || 0;
    const dir = formData.openingBalanceDirection;
    const newBalance = (dir === 'they_owe_you' ? amount : dir === 'you_owe_them' ? -amount : 0);
    const currentBalance = parseFloat(editingParty.balance) || 0;
    return Math.abs(newBalance - currentBalance) >= 0.01;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingParty) {
      if (isOpeningBalanceChanged() && !(formData.balanceEditReason || '').trim()) {
        error('Reason for balance edit is required when changing the opening balance.');
        return;
      }
      setShowUpdateConfirmModal(true);
    } else {
      handleCreateParty();
    }
  };

  const handleCreateParty = async () => {
    if (!hasBranches) {
      error('Create a branch first. Every party is linked to a branch—go to Branches to add one.');
      return;
    }
    if (isAdmin && !effectiveBranchId) {
      error('Select a branch from the header dropdown. The party will be linked to that branch.');
      return;
    }
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
      const amount = parseFloat(formData.openingBalanceAmount) || 0;
      let dir = formData.openingBalanceDirection;
      if (formData.isShopExpense && dir === 'they_owe_you') dir = ''; // Shop expense: we only pay out, never "they owe us"
      const openingBalance = (dir === 'they_owe_you' ? amount : dir === 'you_owe_them' ? -amount : 0);
      const onlyShopExpense = formData.isShopExpense && !formData.isVendor && !formData.isCustomer && !formData.isTailor && !formData.isAdditional && !formData.isEmployee;
      const payload = {
        ...formData,
        businessId: parseInt(businessId),
        openingBalance,
        isEmployee: Boolean(formData.isEmployee),
        salary: formData.salary ? parseFloat(formData.salary) : null
      };
      if (formData.isEmployee) {
        payload.employeeAdvance = formData.employeeAdvance ? parseFloat(formData.employeeAdvance) : 0;
        payload.salaryPaidForCurrentMonth = Boolean(formData.salaryPaidForCurrentMonth);
      }
      if (onlyShopExpense) {
        payload.name = 'Shop Expenses';
        payload.city = '—';
        payload.isVendor = false;
        payload.isCustomer = false;
        payload.isTailor = false;
        payload.isAdditional = false;
        payload.isEmployee = false;
        payload.isShopExpense = true;
      }
      delete payload.openingBalanceDirection;
      delete payload.openingBalanceAmount;
      if (effectiveBranchId) payload.branchId = effectiveBranchId;
      await axios.post(`${API}/parties`, payload);
      success(onlyShopExpense ? 'Shop Expenses ledger is ready. Record expenses with a description for each payment.' : `Party created and linked to ${currentBranch?.name || 'branch'}.`);
      resetForm();
      fetchParties();
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const confirmUpdate = async () => {
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
      const amount = parseFloat(formData.openingBalanceAmount) || 0;
      let dir = formData.openingBalanceDirection;
      if (formData.isShopExpense && dir === 'they_owe_you') dir = ''; // Shop expense: we only pay out, never "they owe us"
      const openingBalance = (dir === 'they_owe_you' ? amount : dir === 'you_owe_them' ? -amount : 0);
      const payload = {
        ...formData,
        businessId: parseInt(businessId),
        openingBalance
      };
      delete payload.openingBalanceDirection;
      delete payload.openingBalanceAmount;
      payload.isVendor = Boolean(formData.isVendor);
      payload.isCustomer = Boolean(formData.isCustomer);
      payload.isTailor = Boolean(formData.isTailor);
      payload.isAdditional = Boolean(formData.isAdditional);
      payload.isEmployee = Boolean(formData.isEmployee);
      payload.isShopExpense = Boolean(formData.isShopExpense);
      payload.salary = formData.salary ? parseFloat(formData.salary) : null;
      if (formData.isEmployee) {
        payload.employeeAdvance = formData.employeeAdvance ? parseFloat(formData.employeeAdvance) : 0;
        payload.salaryPaidForCurrentMonth = Boolean(formData.salaryPaidForCurrentMonth);
      }
      if (isOpeningBalanceChanged()) {
        const reason = (formData.balanceEditReason || '').trim();
        if (!reason) {
          error('Reason for balance edit is required when changing the opening balance.');
          return;
        }
        payload.balanceEditReason = reason;
      }
      // Managers cannot edit balance: do not send opening balance or reason to backend
      if (!canEditPartyBalance && editingParty) {
        delete payload.openingBalance;
        delete payload.balanceEditReason;
      }
      if (effectiveBranchId) payload.branchId = effectiveBranchId;

      await axios.put(`${API}/parties/${editingParty.id}`, payload);
      success('Party updated successfully!');
      setShowUpdateConfirmModal(false);
      resetForm();
      fetchParties();
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingParty(null);
    setFormData({
      name: '',
      phone: '',
      city: '',
      address: '',
      bankAccount: '',
      bankName: '',
      isVendor: false,
      isCustomer: false,
      isTailor: false,
      isAdditional: false,
      isEmployee: false,
      isShopExpense: false,
      salary: '',
      notes: '',
      openingBalanceDirection: '',
      openingBalanceAmount: '',
      balanceEditReason: '',
      employeeAdvance: '',
      salaryPaidForCurrentMonth: false
    });
  };

  const getRoleBadges = (party) => {
    const roles = [];
    if (party.is_vendor) roles.push(<span key="v" className="role-badge vendor">Vendor</span>);
    if (party.is_customer) roles.push(<span key="c" className="role-badge customer">Customer</span>);
    if (party.is_tailor) roles.push(<span key="t" className="role-badge tailor">Tailor</span>);
    if (party.is_additional) roles.push(<span key="a" className="role-badge additional">Additional</span>);
    if (party.is_employee) roles.push(<span key="e" className="role-badge employee">Employee</span>);
    if (party.is_shop_expense) roles.push(<span key="s" className="role-badge shop-expense">Shop Expense</span>);
    return roles;
  };

  const formatBalance = (balance, isShopExpense = false) => {
    if (isShopExpense) return <span className="balance-expense-ledger">Expense ledger</span>;
    const num = parseFloat(balance) || 0;
    if (num > 0) return <span className="balance-positive">{formatPKR(num)} (To receive)</span>;
    if (num < 0) return <span className="balance-negative">{formatPKR(Math.abs(num))} (We owe)</span>;
    return <span className="balance-zero">{formatPKR(0)}</span>;
  };

  const formatBalanceText = (balance, isShopExpense = false) => {
    if (isShopExpense) return 'Expense ledger';
    const num = parseFloat(balance) || 0;
    if (num > 0) return `${formatPKR(num)} (To receive)`;
    if (num < 0) return `${formatPKR(Math.abs(num))} (We owe)`;
    return formatPKR(0);
  };

  const getStatementBillRef = (entry) => {
    const dr = parseFloat(entry.debit) || 0;
    const cr = parseFloat(entry.credit) || 0;
    const amount = dr > 0 ? dr : cr;
    if (entry.sale_details) return `Sale #${entry.sale_details.invoice_number || entry.reference_id} · ${formatPKR(entry.sale_details.total_amount || amount)}`;
    if (entry.purchase_details) return `Purchase #${entry.purchase_details.purchase_number || entry.reference_id} · ${formatPKR(entry.purchase_details.total_amount || amount)}`;
    if (entry.sale_return_details) return `Sale Return #${entry.sale_return_details.return_number} · Refund ${formatPKR(entry.sale_return_details.refund_amount || amount)}`;
    if (entry.transaction_type === 'payment_in' || entry.transaction_type === 'payment_out') {
      const ref = getPaymentReferenceFromMeta(entry.meta);
      return ref ? `Payment · ${formatPKR(amount)} · Ref: ${ref}` : `Payment · ${formatPKR(amount)}`;
    }
    if (entry.transaction_type === 'opening_balance') return `Opening · ${formatPKR(amount)}`;
    if (entry.transaction_type === 'balance_adjustment') return `Balance edited · ${formatPKR(amount)}`;
    return amount > 0 ? formatPKR(amount) : '—';
  };

  const getStatementTypeLabel = (entry) => {
    return entry.entry_type_label || (entry.transaction_type === 'balance_adjustment' ? 'Balance edited' : (entry.transaction_type || '').replace(/_/g, ' ')) || '—';
  };

  const getOpeningBalancePreview = () => {
    const amount = parseFloat(formData.openingBalanceAmount) || 0;
    const dir = formData.openingBalanceDirection;
    if (amount <= 0 || !dir) return null;
    if (formData.isShopExpense && dir === 'they_owe_you') return null; // Shop expense: no "they owe you"
    if (dir === 'they_owe_you') return { text: `${formatPKR(amount)} – Payment Received`, className: 'balance-preview-positive' };
    return { text: `${formatPKR(amount)} – Payment Made`, className: 'balance-preview-negative' };
  };

  const openAddTailorModal = () => {
    if (!hasBranches) return;
    setEditingParty(null);
    setFormData({
      name: '', phone: '', city: '', address: '', bankAccount: '', bankName: '',
      isVendor: false, isCustomer: false, isTailor: true, isAdditional: false, isEmployee: false, isShopExpense: false,
      salary: '', notes: '', openingBalanceDirection: 'you_owe_them', openingBalanceAmount: '', balanceEditReason: '',
      employeeAdvance: '', salaryPaidForCurrentMonth: false
    });
    setShowModal(true);
  };

  const openAddPartyModal = () => {
    if (!hasBranches) return;
    setEditingParty(null);
    setFormData({
      name: '', phone: '', city: '', address: '', bankAccount: '', bankName: '',
      isVendor: false, isCustomer: false, isTailor: false, isAdditional: false, isEmployee: false, isShopExpense: false,
      salary: '', notes: '', openingBalanceDirection: 'you_owe_them', openingBalanceAmount: '', balanceEditReason: '',
      employeeAdvance: '', salaryPaidForCurrentMonth: false
    });
    setShowModal(true);
  };

  return (
    <Layout>
      <div className="parties-page">
        <div className="page-header">
          <div>
            <h1>Parties</h1>
            <p className="page-subtitle">Manage vendors, customers, and tailors</p>
          </div>
          {(user.role === 'super_admin' || user.role === 'admin' || user.role === 'manager' || user.role === 'inventory_manager' || user.role === 'accounts_manager') && (
            <div className="page-header-actions">
              <Tooltip text={!hasBranches ? 'Create a branch first' : 'Add tailor (defaults to Payment Made)'} position="bottom">
                <button
                  type="button"
                  onClick={openAddTailorModal}
                  className="create-button create-button-secondary"
                  disabled={!hasBranches}
                  aria-disabled={!hasBranches}
                  title={!hasBranches ? 'Disabled: create a branch first' : ''}
                >
                  <HiPlus />
                  <span>Add Tailor</span>
                </button>
              </Tooltip>
              <Tooltip text={!hasBranches ? 'Create a branch first—every party is linked to a branch' : 'Add new party (linked to current branch). Opening balance is always asked.'} position="bottom">
                <button
                  onClick={openAddPartyModal}
                  className="create-button"
                  disabled={!hasBranches}
                  aria-disabled={!hasBranches}
                  title={!hasBranches ? 'Disabled: create a branch first' : ''}
                >
                  <HiPlus />
                  <span>Add Party</span>
                </button>
              </Tooltip>
            </div>
          )}
        </div>

        {!hasBranches && (
          <div className="banner no-branches-banner">
            <strong>You need a branch before you can add anything.</strong> Every party, product, bank account, and transaction is linked to a branch. Create your first branch, then come back to add parties. <a href="/branches">Go to Branches →</a>
          </div>
        )}
        <div className="filters-container">
          {user.role === 'super_admin' && (
            <div className="filter-dropdown business-selector">
              <select 
                value={selectedBusinessId} 
                onChange={(e) => setSelectedBusinessId(e.target.value)}
              >
                <option value="">Select Business</option>
                {businesses.map(business => (
                  <option key={business.id} value={business.id}>{business.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="search-bar">
            <HiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, phone, or city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-dropdown">
            <HiFilter className="filter-icon" />
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">All Roles</option>
              <option value="vendor">Vendors</option>
              <option value="customer">Customers</option>
              <option value="tailor">Tailors</option>
              <option value="shop_expense">Shop Expense</option>
            </select>
          </div>
          <button
            type="button"
            className="create-button create-button-secondary"
            onClick={handleDownloadPartiesPdf}
            disabled={partiesPdfLoading}
            title="Download filtered parties PDF"
          >
            <HiDocumentReport />
            <span>{partiesPdfLoading ? 'Preparing...' : 'Download Parties PDF'}</span>
          </button>
        </div>

        {loading ? (
          <Loader message="Loading parties..." variant="block" />
        ) : (
          <div className="table-container">
            {filteredParties.length === 0 ? (
              <div className="empty-state">
                <p>No parties found.</p>
                <Tooltip text={!hasBranches ? 'Create a branch first' : 'Add your first party'} position="top">
                  <button
                    onClick={() => { if (hasBranches) setShowModal(true); }}
                    className="empty-state-button"
                    disabled={!hasBranches}
                  >
                    Add Your First Party
                  </button>
                </Tooltip>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>City</th>
                    <th>Branch</th>
                    <th>Roles</th>
                    <th>Salary</th>
                    <th>Balance</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParties.map((party) => (
                    <tr key={party.id}>
                      <td className="font-semibold">{party.name}</td>
                      <td>{party.phone || '—'}</td>
                      <td>{party.city || '—'}</td>
                      <td>{party.branch_name || '—'}</td>
                      <td><div className="roles-container">{getRoleBadges(party)}</div></td>
                      <td>{party.is_employee && party.salary != null ? formatPKR(party.salary) : '—'}</td>
                      <td>{formatBalance(party.balance, party.is_shop_expense)}</td>
                      <td className="created-at-cell">{formatDate(party.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <Tooltip text="View ledger" position="top">
                            <button className="action-btn view-btn" onClick={() => handleViewLedger(party)}>
                              <HiEye />
                            </button>
                          </Tooltip>
                          <Tooltip text="Statement / Report" position="top">
                            <button className="action-btn statement-btn" onClick={() => openStatement(party)} title="Statement">
                              <HiDocumentReport />
                            </button>
                          </Tooltip>
                          {(user.role === 'super_admin' || user.role === 'admin' || user.role === 'manager' || user.role === 'inventory_manager' || user.role === 'accounts_manager') && (
                            <Tooltip text="Edit party" position="top">
                              <button className="action-btn edit-btn" onClick={() => handleEdit(party)}>
                                <HiPencil />
                              </button>
                            </Tooltip>
                          )}
                          {(user.role === 'super_admin' || user.role === 'admin') && (
                            parseFloat(party.balance) === 0 ? (
                              <Tooltip text="Delete party" position="top">
                                <button className="action-btn delete-btn" onClick={() => handleDelete(party.id)}>
                                  <HiX />
                                </button>
                              </Tooltip>
                            ) : (
                              <Tooltip text="Cannot delete: party has outstanding balance" position="top">
                                <button className="action-btn delete-btn" disabled title="Clear balance to delete">
                                  <HiX />
                                </button>
                              </Tooltip>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={resetForm}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
              <h2>{editingParty ? 'Edit Party' : 'Add New Party'}</h2>
              {!hasBranches ? (
                <div className="banner no-branches-banner" style={{ marginTop: 0 }}>
                  <strong>You need a branch first.</strong> Every party is linked to a branch. Create a branch from <a href="/branches">Branches</a>, then you can add parties here.
                </div>
              ) : null}
              {hasBranches && !editingParty && currentBranch && (
                <p className="party-linked-branch">
                  This party will be linked to: <strong>{currentBranch.name}</strong>
                </p>
              )}
              <form onSubmit={handleSubmit}>
                <div className="form-group roles-first">
                  <label>Roles *</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.isVendor}
                        onChange={(e) => setFormData({ ...formData, isVendor: e.target.checked })}
                      />
                      Vendor
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.isCustomer}
                        onChange={(e) => setFormData({ ...formData, isCustomer: e.target.checked })}
                      />
                      Customer
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.isTailor}
                        onChange={(e) => setFormData({ ...formData, isTailor: e.target.checked })}
                      />
                      Tailor
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.isAdditional}
                        onChange={(e) => setFormData({ ...formData, isAdditional: e.target.checked })}
                      />
                      Additional
                    </label>
                    {editingParty && (
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.isEmployee}
                        onChange={(e) => setFormData({ ...formData, isEmployee: e.target.checked })}
                      />
                      Employee
                    </label>
                    )}
                    {!parties.some(p => p.is_shop_expense) && (
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.isShopExpense}
                        onChange={(e) => setFormData({ ...formData, isShopExpense: e.target.checked })}
                      />
                      Shop Expense
                    </label>
                  )}
                  </div>
                  {formData.isShopExpense && (
                    <small className="form-hint" style={{ display: 'block', marginTop: 6 }}>
                      One Shop Expenses ledger per business. No name or contact fields — only optional opening balance below. Every payment recorded must include a description (e.g. rent, electricity, food).
                    </small>
                  )}
                </div>
                {(() => {
                  const onlyShopExpense = !editingParty && formData.isShopExpense && !formData.isVendor && !formData.isCustomer && !formData.isTailor && !formData.isAdditional && !formData.isEmployee;
                  if (onlyShopExpense) {
                    return null;
                  }
                  return (
                    <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g. Ahmed Textiles"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="e.g. +92 300 1234567"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City {!editingParty ? '*' : ''}</label>
                    <CitySelect
                      value={formData.city}
                      onChange={(city) => setFormData({ ...formData, city })}
                      required={!editingParty}
                      placeholder="Select city"
                      className="form-input"
                      onError={error}
                    />
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="e.g. Shop #12, Anarkali"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Bank Name</label>
                    <input
                      type="text"
                      value={formData.bankName}
                      onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                      placeholder="e.g. HBL"
                    />
                  </div>
                  <div className="form-group">
                    <label>Bank Account</label>
                    <input
                      type="text"
                      value={formData.bankAccount}
                      onChange={(e) => setFormData({ ...formData, bankAccount: e.target.value })}
                      placeholder="e.g. 1234567890"
                    />
                  </div>
                </div>
                    </>
                  );
                })()}
                {(!editingParty || canEditPartyBalance) && (
                <div className="form-group opening-balance-group">
                  <label>Opening Balance</label>
                  <>
                    <div className="opening-balance-options">
                      {!formData.isShopExpense && (
                      <label className={`opening-balance-option ${formData.openingBalanceDirection === 'they_owe_you' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="openingBalanceDirection"
                          checked={formData.openingBalanceDirection === 'they_owe_you'}
                          onChange={() => setFormData({ ...formData, openingBalanceDirection: 'they_owe_you' })}
                        />
                        <span className="option-text">
                          <span className="option-label">Payment Received</span>
                        </span>
                      </label>
                      )}
                      <label className={`opening-balance-option ${formData.openingBalanceDirection === 'you_owe_them' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="openingBalanceDirection"
                          checked={formData.openingBalanceDirection === 'you_owe_them'}
                          onChange={() => setFormData({ ...formData, openingBalanceDirection: 'you_owe_them' })}
                        />
                        <span className="option-text">
                          <span className="option-label">Payment Made</span>
                        </span>
                      </label>
                    </div>
                    <div className="opening-balance-amount-row">
                      <label className="amount-label">Amount (PKR)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.openingBalanceAmount}
                        onChange={(e) => setFormData({ ...formData, openingBalanceAmount: e.target.value })}
                        placeholder="0.00"
                        disabled={!formData.openingBalanceDirection}
                        className="opening-balance-amount-input"
                      />
                    </div>
                    {getOpeningBalancePreview() && (
                      <div className={`opening-balance-preview ${getOpeningBalancePreview().className}`}>
                        {getOpeningBalancePreview().text}
                      </div>
                    )}
                    {editingParty && canEditPartyBalance && isOpeningBalanceChanged() && (
                      <div className="form-group" style={{ marginTop: 12 }}>
                        <label>Reason for balance edit *</label>
                        <input
                          type="text"
                          value={formData.balanceEditReason}
                          onChange={(e) => setFormData({ ...formData, balanceEditReason: e.target.value })}
                          placeholder="e.g. Correction after reconciliation"
                          required
                        />
                        <small className="form-hint">This reason will appear in the ledger and statement for audit.</small>
                      </div>
                    )}
                    </>
                </div>
                )}
                {formData.isEmployee && !formData.isShopExpense && (
                  <>
                    <div className="form-group">
                      <label>Salary (optional)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.salary}
                        onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                        placeholder="e.g. 30000"
                      />
                      <small className="form-hint">Monthly salary in Rs.</small>
                    </div>
                    <div className="form-group">
                      <label>Advance (optional)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.employeeAdvance}
                        onChange={(e) => setFormData({ ...formData, employeeAdvance: e.target.value })}
                        placeholder="e.g. 5000"
                      />
                      <small className="form-hint">Any amount already given to this person as advance (will be reflected in their balance).</small>
                    </div>
                    <div className="form-group">
                      <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="checkbox"
                          checked={formData.salaryPaidForCurrentMonth}
                          onChange={(e) => setFormData({ ...formData, salaryPaidForCurrentMonth: e.target.checked })}
                        />
                        Salary for current month already paid
                      </label>
                      <small className="form-hint">If checked, recording salary for the current month will not deduct from any bank/cash account.</small>
                    </div>
                  </>
                )}
                {!(formData.isShopExpense && !formData.isVendor && !formData.isCustomer && !formData.isTailor && !formData.isAdditional && !formData.isEmployee) && (
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any additional notes..."
                  />
                </div>
                )}
                <div className="modal-actions">
                  <button type="button" onClick={resetForm}>Cancel</button>
                  <button type="submit" disabled={!editingParty && !hasBranches}>{editingParty ? 'Review changes' : 'Create'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Update Confirmation Modal */}
        {showUpdateConfirmModal && editingParty && (
          <div className="modal-overlay" onClick={() => setShowUpdateConfirmModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Confirm Update</h2>
              <p className="update-confirm-intro">Review the changes below. Click "Confirm Update" to apply, or "Back" to continue editing.</p>
              <div className="update-changes-list">
                {getUpdateChanges().length === 0 ? (
                  <p className="no-changes">No changes detected.</p>
                ) : (
                  <table className="update-changes-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Current</th>
                        <th>New</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getUpdateChanges().map((c, i) => (
                        <tr key={i}>
                          <td><strong>{c.label}</strong></td>
                          <td className="change-before">{c.before}</td>
                          <td className="change-after">{c.after}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowUpdateConfirmModal(false)}>Back</button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={confirmUpdate}
                  disabled={getUpdateChanges().length === 0}
                >
                  Confirm Update
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Ledger Modal */}
        {showLedgerModal && selectedParty && (
          <div className="modal-overlay" onClick={() => setShowLedgerModal(false)}>
            <div className="modal-content modal-xlarge" onClick={(e) => e.stopPropagation()}>
              <h2>Ledger - {selectedParty.name}</h2>
              <div className="ledger-summary">
                <p><strong>Current Balance:</strong> {formatBalance(selectedParty.balance, selectedParty.is_shop_expense)}</p>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  {selectedParty.is_shop_expense
                    ? 'Expense ledger — you only record payments you make (e.g. rent, electricity). There is nothing they owe you.'
                    : parseFloat(selectedParty.balance) > 0
                    ? 'This party owes you money (to receive)'
                    : parseFloat(selectedParty.balance) < 0
                    ? 'You owe money to this party'
                    : 'No outstanding balance'}
                </p>
                <div className="ledger-statement-actions">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => handlePrintStatement(selectedParty)}
                    disabled={statementPdfLoading}
                    title="Print full statement (opens print dialog)"
                  >
                    <HiPrinter /> {statementPdfLoading ? 'Preparing…' : 'Print statement'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => handleDownloadPdfStatement(selectedParty)}
                    disabled={statementPdfLoading}
                    title="Download full statement as PDF"
                  >
                    <HiDocumentReport /> {statementPdfLoading ? 'Preparing…' : 'Download PDF'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={async () => {
                      await fetchPartyAuditHistory(selectedParty);
                      setShowPartyAuditModal(true);
                    }}
                    disabled={partyAuditLoading}
                    title="View audit history for this party"
                  >
                    {partyAuditLoading ? 'Loading audit…' : 'Audit history'}
                  </button>
                </div>
              </div>
              <div className="ledger-table-container">
                {ledgerLoading ? (
                  <p className="ledger-party-cheques-loading">Loading ledger…</p>
                ) : ledgerEntries.length === 0 ? (
                  <p className="no-entries">No ledger entries found.</p>
                ) : (
                  <>
                    <table className="ledger-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Debit</th>
                          <th>Credit</th>
                          <th>Balance</th>
                          <th>Receipt</th>
                          {isAdmin && <th>Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {ledgerEntries.map((entry) => (
                          <tr key={entry.id}>
                            <td>{new Date(entry.transaction_date).toLocaleDateString()}</td>
                            <td><span className={`type-badge ${entry.transaction_type}`}>{entry.transaction_type === 'balance_adjustment' ? 'Balance edited' : (entry.transaction_type || '').replace(/_/g, ' ')}</span></td>
                            <td>
                              <div>{entry.description || '—'}</div>
                              {(entry.transaction_type === 'payment_in' || entry.transaction_type === 'payment_out') && (() => {
                                const ref = getPaymentReferenceFromMeta(entry.meta);
                                const pm = entry.meta?.paymentMethod;
                                if (!pm && !ref) return null;
                                return (
                                  <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                                    {pm && `Method: ${pm}`}
                                    {ref && <>{pm ? ' · ' : null}Ref: {ref}</>}
                                  </small>
                                );
                              })()}
                              {(() => {
                                const meta = entry?.meta || {};
                                const reason = meta.reversalReason || meta.reversal_reason || meta.reason;
                                if (!reason) return null;
                                return (
                                  <small style={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', marginTop: '0.15rem' }}>
                                    Reason: {reason}
                                  </small>
                                );
                              })()}
                            </td>
                            <td className="debit">{parseFloat(entry.debit) > 0 ? formatPKR(entry.debit) : '—'}</td>
                            <td className="credit">{parseFloat(entry.credit) > 0 ? formatPKR(entry.credit) : '—'}</td>
                            <td>{selectedParty?.is_shop_expense ? '—' : formatBalance(entry.balance_after)}</td>
                            <td>
                              <button
                                type="button"
                                className="btn-sm btn-secondary"
                                onClick={() => handleDownloadLedgerEntryReceipt(entry)}
                                disabled={ledgerReceiptDownloadingId === entry.id}
                                title="Download receipt for this ledger entry"
                              >
                                <HiPrinter /> {ledgerReceiptDownloadingId === entry.id ? 'Preparing…' : 'Receipt'}
                              </button>
                            </td>
                            {isAdmin && (
                              <td>
                                {(() => {
                                  const meta = entry?.meta || {};
                                  const isReversal = entry?.reference_type === 'ledger_reversal'
                                    || meta?.isReversal === true || meta?.is_reversal === true
                                    || meta?.reversalOfLedgerId || meta?.reversal_of_ledger_id;
                                  const alreadyReversed = meta?.reversedAt || meta?.reversed_at || meta?.reversedByLedgerId || meta?.reversed_by_ledger_id;
                                  const disabled = isReversal || alreadyReversed || reversingLedgerId === entry.id;
                                  return (
                                    <button
                                      type="button"
                                      className="btn-sm btn-secondary"
                                      onClick={() => handleReverseLedgerEntry(entry)}
                                      disabled={disabled}
                                      title={isReversal ? 'Reversal entries cannot be reversed again' : (alreadyReversed ? 'Already reversed' : 'Reverse this ledger entry')}
                                    >
                                      {reversingLedgerId === entry.id ? 'Reversing…' : (alreadyReversed ? 'Reversed' : 'Reverse')}
                                    </button>
                                  );
                                })()}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {ledgerHasMore && (
                      <div className="ledger-load-more-wrap">
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={() => fetchLedger(selectedParty?.id, true)}
                          disabled={ledgerLoadingMore}
                        >
                          {ledgerLoadingMore ? 'Loading…' : `Load more (${ledgerEntries.length} of ${ledgerTotal})`}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="ledger-party-cheques">
                <h3 className="ledger-party-cheques-title">Cheques from / to this party</h3>
                <p className="ledger-party-cheques-hint">Cheques we received from this party or we gave to this party (e.g. when you pay a vendor by cheque). All appear in the ledger. Pending/upcoming: &quot;Turn into cash&quot; (received) or &quot;Clear cheque&quot; (we gave — bank is debited).</p>
                {partyChequesLoading ? (
                  <p className="ledger-party-cheques-loading">Loading cheques…</p>
                ) : partyCheques.length === 0 ? (
                  <p className="no-entries">No cheques from or to this party.</p>
                ) : (
                  <>
                    <div className="ledger-party-cheques-sort">
                      <label>Sort by:</label>
                      <select
                        value={partyChequesSortBy}
                        onChange={e => setPartyChequesSortBy(e.target.value)}
                        className="ledger-party-cheques-sort-select"
                      >
                        <option value="date">Date</option>
                        <option value="amount">Amount</option>
                        <option value="status">Status</option>
                        <option value="cashed_date">Cashed date</option>
                      </select>
                      <select
                        value={partyChequesSortOrder}
                        onChange={e => setPartyChequesSortOrder(e.target.value)}
                        className="ledger-party-cheques-sort-select"
                      >
                        <option value="asc">Ascending</option>
                        <option value="desc">Descending</option>
                      </select>
                    </div>
                    <div className="ledger-party-cheques-table-wrap">
                      <table className="ledger-table party-cheques-table">
                        <thead>
                          <tr>
                            <th>Direction</th>
                            <th>Date</th>
                            <th>Cheque #</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Cashed date</th>
                            <th className="party-cheques-col-action">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedPartyCheques.map((c) => {
                            const isGiven = (c.payment_type || c.paymentType) === 'payment_out';
                            return (
                            <tr key={c.id}>
                              <td>
                                <span className={`party-cheque-direction party-cheque-direction--${isGiven ? 'given' : 'received'}`}>
                                  {isGiven ? 'We gave' : 'We received'}
                                </span>
                              </td>
                              <td>{c.cheque_date ? formatDate(c.cheque_date) : (c.created_at ? formatDate(c.created_at) : '—')}</td>
                              <td className="mono">{c.cheque_number || '—'}</td>
                              <td className="amount">{formatPKR(c.amount)}</td>
                              <td>
                                <span className={`party-cheque-status party-cheque-status--${(c.display_status || c.status || '').toLowerCase()}`}>
                                  {(c.display_status || c.status) === 'cashed' ? 'Cashed' : (c.display_status || c.status) === 'deleted' ? 'Deleted' : (c.display_status || c.status) === 'upcoming' ? 'Upcoming' : 'Pending'}
                                </span>
                              </td>
                              <td>{c.approved_at ? formatDate(c.approved_at) : '—'}</td>
                              <td className="party-cheques-col-action">
                                {((c.display_status || c.status || '').toLowerCase() === 'pending' || (c.display_status || c.status || '').toLowerCase() === 'upcoming') && (
                                  <div className="party-cheque-actions">
                                    <button
                                      type="button"
                                      className="btn-sm btn-secondary party-cheque-turn-into-cash"
                                      onClick={() => handleTurnIntoCash(c)}
                                      title={isGiven ? 'Mark cheque as cleared (bank will be debited)' : 'Turn into cash and choose where to deposit (single or split)'}
                                    >
                                      <HiCheck /> {isGiven ? 'Clear cheque' : 'Turn into cash'}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-sm btn-secondary party-cheque-delete"
                                      onClick={() => handleDeletePartyCheque(c)}
                                      title="Delete this cheque"
                                    >
                                      <HiX /> Delete
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ); })}
                        </tbody>
                      </table>
                    </div>
                    {partyChequesHasMore && (
                      <div className="ledger-load-more-wrap">
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={() => fetchPartyCheques(selectedParty?.id, true)}
                          disabled={partyChequesLoadingMore}
                        >
                          {partyChequesLoadingMore ? 'Loading…' : `Load more cheques (${partyCheques.length} of ${partyChequesTotal})`}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-actions">
                {(user.role === 'super_admin' || user.role === 'admin' || user.role === 'manager') && (
                  <>
                    <button 
                      className="btn-primary" 
                      onClick={() => handleRecordPayment(selectedParty)}
                      style={{ marginRight: 'auto' }}
                    >
                      <HiCurrencyRupee /> Record Payment
                    </button>
                    {(selectedParty.is_vendor || selectedParty.isVendor) && (
                      <button 
                        type="button"
                        className="btn-secondary"
                        onClick={handleAllProductsAvailable}
                        title="Return unsold products to this vendor (per their policy). Original prices are pre-filled and editable. Completing the return updates inventory and P&L."
                      >
                        All products available
                      </button>
                    )}
                  </>
                )}
                <button onClick={() => setShowLedgerModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Party Audit Modal */}
        {showPartyAuditModal && selectedParty && (
          <div className="modal-overlay" onClick={() => setShowPartyAuditModal(false)}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
              <h2>Audit history - {selectedParty.name}</h2>
              <p style={{ marginTop: '-0.25rem', color: '#6b7280', fontSize: '0.9rem' }}>
                This is an audit trail of actions performed in the system for this party.
              </p>
              <div style={{ marginTop: '0.75rem' }}>
                {partyAuditLoading ? (
                  <p>Loading…</p>
                ) : partyAuditEntries.length === 0 ? (
                  <p className="no-entries">No audit entries found.</p>
                ) : (
                  <div style={{ maxHeight: '60vh', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
                    <table className="ledger-table" style={{ margin: 0 }}>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>User</th>
                          <th>Role</th>
                          <th>Action</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partyAuditEntries.map((a) => (
                          <tr key={a.id}>
                            <td>{a.created_at ? new Date(a.created_at).toLocaleString() : '—'}</td>
                            <td>{a.user_name || a.userName || '—'}</td>
                            <td>{a.user_role || a.userRole || '—'}</td>
                            <td>{a.action || '—'}</td>
                            <td>{a.description || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowPartyAuditModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Approve cheque from Ledger: Where to deposit? (received) or Clear cheque (we gave) */}
        {chequeToApprove && (() => {
          const isGiven = (chequeToApprove.payment_type || chequeToApprove.paymentType) === 'payment_out';
          const chequeAmount = parseFloat(chequeToApprove.amount) || 0;
          const total = approveSplits.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
          const accountOptions = fundAccounts || [];
          const debitAccount = isGiven && chequeToApprove.account_id ? accountOptions.find(a => String(a.id) === String(chequeToApprove.account_id)) : null;
          return (
            <div className="modal-overlay" onClick={() => !approveSubmitting && setChequeToApprove(null)}>
              <div className="modal-content accounts-modal accounts-approve-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{isGiven ? 'Clear cheque (we gave)' : 'Where to deposit?'}</h3>
                  <button type="button" className="modal-close" onClick={() => !approveSubmitting && setChequeToApprove(null)}><HiX /></button>
                </div>
                {isGiven ? (
                  <>
                    <p className="form-hint">This will debit the bank account you selected when recording this payment.{debitAccount ? ` (${debitAccount.name})` : ''} Party ledger already shows this payment.</p>
                    <p className="approve-cheque-amount"><strong>Cheque amount:</strong> {formatPKR(chequeAmount)}</p>
                    <form onSubmit={handleApproveChequeFromLedger}>
                      <div className="modal-actions">
                        <button type="button" onClick={() => !approveSubmitting && setChequeToApprove(null)}>Cancel</button>
                        <button type="submit" className="btn-primary" disabled={approveSubmitting}>{approveSubmitting ? 'Clearing…' : 'Clear cheque'}</button>
                      </div>
                    </form>
                  </>
                ) : (
                  <>
                <p className="form-hint">This cheque is being turned into cash. Choose where to deposit the payment: you can add it to a single account or split it across multiple accounts. Party ledger will be updated and each selected account will be credited.</p>
                <p className="approve-cheque-amount"><strong>Cheque amount:</strong> {formatPKR(chequeAmount)}</p>
                <form onSubmit={handleApproveChequeFromLedger}>
                  <div className="form-group">
                    <div className="approve-splits-header">
                      <label>Credit to accounts</label>
                      <button
                        type="button"
                        className="accounts-btn accounts-btn--secondary btn-sm"
                        onClick={() => setApproveSplits(prev => [...prev, { accountId: '', amount: '' }])}
                      >
                        <HiPlus className="accounts-btn__icon" /> Add split
                      </button>
                    </div>
                    <div className="approve-splits-table-wrap">
                      <table className="approve-splits-table">
                        <thead>
                          <tr>
                            <th>Account</th>
                            <th>Payment</th>
                            <th className="approve-splits-col-action"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {approveSplits.map((split, idx) => {
                            const selectedInOtherRows = new Set(
                              approveSplits
                                .map((s, i) => (i !== idx && s.accountId ? String(s.accountId) : null))
                                .filter(Boolean)
                            );
                            const availableAccounts = accountOptions.filter(
                              a => String(a.id) === String(split.accountId) || !selectedInOtherRows.has(String(a.id))
                            );
                            const selectedAccount = split.accountId ? accountOptions.find(a => String(a.id) === String(split.accountId)) : null;
                            return (
                              <tr key={idx} className="approve-split-row">
                                <td className="approve-split-col-account">
                                  <select
                                    value={split.accountId}
                                    onChange={e => setApproveSplits(prev => {
                                      const next = [...prev];
                                      next[idx] = { ...next[idx], accountId: e.target.value };
                                      return next;
                                    })}
                                    className="approve-split-select"
                                  >
                                    <option value="">Select account</option>
                                    {availableAccounts.map(a => (
                                      <option key={a.id} value={a.id}>{a.name} ({a.type}) – {formatPKR(a.current_balance ?? a.currentBalance ?? 0)}</option>
                                    ))}
                                  </select>
                                  {selectedAccount && (
                                    <span className="approve-split-account-display">{selectedAccount.name}</span>
                                  )}
                                </td>
                                <td className="approve-split-col-payment">
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="Amount"
                                    value={split.amount}
                                    onChange={e => setApproveSplits(prev => {
                                      const next = [...prev];
                                      next[idx] = { ...next[idx], amount: e.target.value };
                                      return next;
                                    })}
                                    className="approve-split-input"
                                  />
                                  {selectedAccount && split.amount !== '' && !Number.isNaN(parseFloat(split.amount)) && (
                                    <span className="approve-split-payment-display">{formatPKR(parseFloat(split.amount))}</span>
                                  )}
                                </td>
                                <td className="approve-splits-col-action">
                                  {approveSplits.length > 1 && (
                                    <button
                                      type="button"
                                      className="btn-remove-split"
                                      onClick={() => setApproveSplits(prev => prev.filter((_, i) => i !== idx))}
                                      aria-label="Remove split"
                                    >
                                      <HiX />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className={`approve-splits-total ${Math.abs(total - chequeAmount) > 0.01 && total > 0 ? 'error' : ''}`}>
                      Total: {formatPKR(total)} {chequeAmount > 0 && (Math.abs(total - chequeAmount) > 0.01 ? ` (must equal ${formatPKR(chequeAmount)})` : '')}
                    </p>
                  </div>
                  {(() => {
                    const summaryRows = approveSplits
                      .filter(s => s.accountId && (parseFloat(s.amount) || 0) > 0)
                      .map(s => ({
                        account: accountOptions.find(a => String(a.id) === String(s.accountId)),
                        amount: parseFloat(s.amount) || 0
                      }))
                      .filter(r => r.account);
                    const summaryTotal = summaryRows.reduce((sum, r) => sum + r.amount, 0);
                    const isComplete = Math.abs(summaryTotal - chequeAmount) <= 0.01;
                    return (
                      <div className="approve-deposit-summary">
                        <h4 className="approve-deposit-summary-title">Deposit summary</h4>
                        <p className="approve-deposit-summary-cheque">Cheque amount: <strong>{formatPKR(chequeAmount)}</strong></p>
                        {summaryRows.length > 0 && (
                          <ul className="approve-deposit-summary-list">
                            {summaryRows.map((r, i) => (
                              <li key={i}>
                                <span className="approve-deposit-summary-account">{r.account.name}</span>
                                <span className="approve-deposit-summary-amount">{formatPKR(r.amount)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <p className={`approve-deposit-summary-total ${!isComplete && summaryTotal > 0 ? 'error' : ''}`}>
                          Total to deposit: <strong>{formatPKR(summaryTotal)}</strong>
                          {chequeAmount > 0 && !isComplete && summaryTotal > 0 && (
                            <span className="approve-deposit-summary-warn"> (must equal {formatPKR(chequeAmount)})</span>
                          )}
                        </p>
                      </div>
                    );
                  })()}
                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={() => setChequeToApprove(null)}>Cancel</button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={approveSubmitting || (chequeAmount > 0 && Math.abs(total - chequeAmount) > 0.01)}
                      title={chequeAmount > 0 && Math.abs(total - chequeAmount) > 0.01 ? `Split total (${formatPKR(total)}) must equal cheque amount (${formatPKR(chequeAmount)})` : ''}
                    >
                      {approveSubmitting ? 'Depositing…' : 'Deposit'}
                    </button>
                  </div>
                </form>
                  </>
                )}
              </div>
            </div>
          );
        })()}

        {/* Return to vendor modal – all products from this vendor */}
        {hasVendorReturnForm && (
          <div className="modal-overlay" onClick={() => { closePurchaseReturnFlow(); setShowReturnFormModal(false); }}>
            <div className="modal-content modal-xlarge" onClick={(e) => e.stopPropagation()}>
              <h2>Return to vendor – {selectedParty?.name}</h2>
              <p className="return-form-hint">
                Return unsold products to this vendor (per their policy). Select products and quantities; original purchase prices are pre-filled and can be edited. Completing the return updates inventory, vendor balance, and affects business P&L.
              </p>
              {loadingVendorPurchases ? (
                <Loader message="Loading products…" variant="block" />
              ) : returnFormData.items.length === 0 ? (
                <>
                  <p className="no-entries">No confirmed purchases found for this vendor in the selected branch.</p>
                  <div className="modal-actions">
                    <button type="button" onClick={closePurchaseReturnFlow}>Close</button>
                  </div>
                </>
              ) : (
                <form onSubmit={handleReturnFormSubmit} className="vendor-return-pos-form">
                  <div className="vendor-return-meta">
                    <div className="form-group">
                      <label>Return date</label>
                      <input
                        type="date"
                        value={returnFormData.returnDate}
                        onChange={(e) => setReturnFormData((prev) => ({ ...prev, returnDate: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="vendor-return-pos-layout">
                    <section className="vendor-return-products">
                      <div className="vendor-return-search-wrap">
                        <HiSearch className="vendor-return-search-icon" />
                        <input
                          type="text"
                          placeholder="Search by product name or code..."
                          value={returnSearchTerm}
                          onChange={(e) => setReturnSearchTerm(e.target.value)}
                          className="vendor-return-search-input"
                          aria-label="Search products"
                        />
                      </div>
                      {(() => {
                        const q = (returnSearchTerm || '').trim().toLowerCase();
                        const filtered = q
                          ? returnFormData.items.filter((it) => {
                              const name = (it.name || '').toLowerCase();
                              const code = (it.productCode || '').toLowerCase();
                              const variant = (it.variantLabel || '').toLowerCase();
                              const purchaseNum = String(it.purchaseNumber || '').toLowerCase();
                              return name.includes(q) || code.includes(q) || variant.includes(q) || purchaseNum.includes(q);
                            })
                          : returnFormData.items;
                        return filtered.length === 0 ? (
                          <p className="vendor-return-no-match">
                            {returnFormData.items.length === 0 ? 'No products.' : 'No products match your search.'}
                          </p>
                        ) : (
                          <div className="vendor-return-product-list">
                            {filtered.map((it) => {
                              const inCart = returnCart.some((c) => c.purchaseItemId === it.purchaseItemId && c.purchaseId === it.purchaseId);
                              const cartEntry = returnCart.find((c) => c.purchaseItemId === it.purchaseItemId && c.purchaseId === it.purchaseId);
                              const atMax = cartEntry ? (parseFloat(cartEntry.returnQty) || 0) >= (it.max || 0) : false;
                              return (
                                <div key={`${it.purchaseId}-${it.purchaseItemId}`} className="vendor-return-product-card">
                                  <div className="vendor-return-product-info">
                                    <span className="vendor-return-product-code">{it.productCode}</span>
                                    <span className="vendor-return-product-name">{it.name}</span>
                                    {it.variantLabel && it.variantLabel !== '—' && (
                                      <span className="vendor-return-product-variant">{it.variantLabel}</span>
                                    )}
                                    <span className="vendor-return-product-meta">Purchase #{it.purchaseNumber} · Max: {it.max}</span>
                                    <span className="vendor-return-product-price">{formatPKR(it.unitPrice)}</span>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn-primary btn-sm vendor-return-add-btn"
                                    onClick={() => addToReturnCart(it)}
                                    disabled={atMax}
                                    title={atMax ? 'Already at max quantity' : 'Add to return'}
                                  >
                                    <HiPlus /> {inCart ? 'Add more' : 'Add to return'}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </section>
                    <aside className="vendor-return-cart">
                      <h3 className="vendor-return-cart-title">Items to return ({returnCart.length})</h3>
                      {returnCart.length === 0 ? (
                        <p className="vendor-return-cart-empty">Cart is empty. Search and add products to return.</p>
                      ) : (
                        <ul className="vendor-return-cart-list">
                          {returnCart.map((item, index) => (
                            <li key={`${item.purchaseId}-${item.purchaseItemId}`} className="vendor-return-cart-item">
                              <div className="vendor-return-cart-item-detail">
                                <div className="vendor-return-cart-item-main">
                                  <span className="vendor-return-cart-item-name">
                                    {item.productCode && <span className="vendor-return-cart-code">{item.productCode}</span>}
                                    <span className="vendor-return-cart-name">{item.name}</span>
                                    {item.variantLabel && item.variantLabel !== '—' && (
                                      <span className="vendor-return-cart-variant">{item.variantLabel}</span>
                                    )}
                                    <span className="vendor-return-cart-meta">Qty: {item.returnQty} (max {item.max})</span>
                                  </span>
                                  <span className="vendor-return-cart-line-total">{formatPKR(item.total || 0)}</span>
                                </div>
                                {returnCartEditIndex === index ? (
                                  <div className="vendor-return-cart-edit-row">
                                    <div className="vendor-return-cart-edit-field">
                                      <label>Qty</label>
                                      <input
                                        type="number"
                                        min={0}
                                        max={item.max}
                                        step={1}
                                        value={item.returnQty}
                                        onChange={(e) => updateReturnCartItem(index, 'returnQty', e.target.value)}
                                        className={returnQtyErrors[index] ? 'input-error' : ''}
                                      />
                                      {returnQtyErrors[index] && <span className="field-error">{returnQtyErrors[index]}</span>}
                                    </div>
                                    <div className="vendor-return-cart-edit-field">
                                      <label>Unit price</label>
                                      <input
                                        type="number"
                                        min={0}
                                        step={0.01}
                                        value={item.unitPrice}
                                        onChange={(e) => updateReturnCartItem(index, 'unitPrice', e.target.value)}
                                      />
                                    </div>
                                    <div className="vendor-return-cart-edit-actions">
                                      <button type="button" className="btn-secondary btn-sm" onClick={() => setReturnCartEditIndex(null)}>Done</button>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                              <div className="vendor-return-cart-actions">
                                {returnCartEditIndex !== index && (
                                  <button type="button" className="vendor-return-cart-edit-btn" onClick={() => setReturnCartEditIndex(index)}>Edit</button>
                                )}
                                <button type="button" className="vendor-return-cart-remove" onClick={() => removeFromReturnCart(index)} aria-label="Remove">
                                  <HiX />
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                      {returnCart.length > 0 && (
                        <div className="vendor-return-cart-subtotal">
                          <strong>Subtotal:</strong> {formatPKR(returnCart.reduce((sum, it) => sum + (parseFloat(it.total) || 0), 0))}
                        </div>
                      )}
                      <div className="form-group vendor-return-reason">
                        <label>Reason (optional)</label>
                        <input
                          type="text"
                          value={returnFormData.reason}
                          onChange={(e) => setReturnFormData((prev) => ({ ...prev, reason: e.target.value }))}
                          placeholder="Reason for return"
                        />
                      </div>
                      <div className="form-group vendor-return-notes">
                        <label>Notes (optional)</label>
                        <textarea
                          value={returnFormData.notes}
                          onChange={(e) => setReturnFormData((prev) => ({ ...prev, notes: e.target.value }))}
                          placeholder="Notes"
                          rows={2}
                        />
                      </div>
                      <div className="vendor-return-cart-actions-bottom">
                        <button type="button" onClick={closePurchaseReturnFlow}>Cancel</button>
                        <button type="submit" className="btn-primary" disabled={submittingReturn || returnCart.length === 0}>
                          {submittingReturn ? 'Creating…' : 'Create return'}
                        </button>
                      </div>
                    </aside>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Party Statement / Report Modal */}
        {showStatementModal && statementParty && (
          <div className="modal-overlay" onClick={() => setShowStatementModal(false)}>
            <div className="modal-content modal-xlarge statement-modal" onClick={(e) => e.stopPropagation()}>
              <h2>Statement – {(statementData?.party?.name ?? statementParty.name) || 'Party'}</h2>
              <p className="statement-party-meta">
                City: {(statementData?.party?.city != null && String(statementData.party.city).trim() !== '')
                  ? statementData.party.city
                  : (statementParty.city != null && String(statementParty.city).trim() !== '' ? statementParty.city : '—')}
                {' · '}
                Phone: {(statementData?.party?.phone != null && String(statementData.party.phone).trim() !== '')
                  ? statementData.party.phone
                  : (statementParty.phone != null && String(statementParty.phone).trim() !== '' ? statementParty.phone : '—')}
              </p>
              <div className="statement-controls">
                <div className="form-group">
                  <label>From</label>
                  <input
                    type="date"
                    value={statementFrom}
                    onChange={(e) => setStatementFrom(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>To</label>
                  <input
                    type="date"
                    value={statementTo}
                    onChange={(e) => setStatementTo(e.target.value)}
                  />
                </div>
                <button type="button" className="btn-primary" onClick={handleStatementApply} disabled={statementLoading}>
                  {statementLoading ? 'Loading…' : 'Apply'}
                </button>
              </div>
              <p className="statement-hint">Ledger of buying and selling with <strong>products and variants</strong> per bill. Each sale or purchase shows its bill reference (Sale # / Purchase #) and a line-by-line breakdown of products, variants, quantities, and amounts.</p>
              {statementLoading && !statementData ? (
                <Loader message="Loading statement…" variant="block" />
              ) : statementData ? (
                <div className="statement-body">
                  <div className="statement-period">
                    Period: {statementData.statement_from} to {statementData.statement_to}
                  </div>
                  <div className="statement-table-wrap">
                    <table className="ledger-table statement-table">
                      <colgroup>
                        <col className="col-date" style={{ width: '9%' }} />
                        <col className="col-type" style={{ width: '10%' }} />
                        <col className="col-bill" style={{ width: '15%' }} />
                        <col className="col-desc" style={{ width: '27%' }} />
                        <col className="col-debit" style={{ width: '10%' }} />
                        <col className="col-credit" style={{ width: '10%' }} />
                        <col className="col-payment" style={{ width: '10%' }} />
                        <col className="col-balance" style={{ width: '9%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="col-date">Date</th>
                          <th className="col-type">Type</th>
                          <th className="col-bill">Bill / Reference</th>
                          <th className="col-desc">Description</th>
                          <th className="col-debit">Amount (Dr)</th>
                          <th className="col-credit">Amount paid (Cr)</th>
                          <th className="col-payment">Payment / Cheque</th>
                          <th className="col-balance">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const ledger = statementData.ledger || [];
                          const isInvoiceType = (e) => e.reference_type === 'sale' || e.reference_type === 'purchase' || e.transaction_type === 'opening_balance';
                          const paymentsAfterByIndex = {};
                          for (let i = 0; i < ledger.length; i++) {
                            if (!isInvoiceType(ledger[i])) continue;
                            const payments = [];
                            for (let j = i + 1; j < ledger.length; j++) {
                              if (isInvoiceType(ledger[j])) break;
                              const e = ledger[j];
                              if (e.transaction_type !== 'payment_in' && e.transaction_type !== 'payment_out') continue;
                              const meta = e.meta || {};
                              const splits = meta.paymentSplits ?? meta.payment_splits;
                              const cr = parseFloat(e.credit) || 0;
                              const dr = parseFloat(e.debit) || 0;
                              const amt = cr > 0 ? cr : dr;
                              if (Array.isArray(splits) && splits.length) {
                                splits.forEach(s => {
                                  const a = parseFloat(s.amount) || 0;
                                  if (a <= 0) return;
                                  const pm = s.paymentMethod ?? s.payment_method;
                                  const cn = s.chequeNumber ?? s.cheque_number;
                                  payments.push({ amount: a, method: pm === 'cheque' ? (cn ? `Cheque #${cn}` : 'Cheque') : (pm || '—') });
                                });
                              } else {
                                const pm = meta.paymentMethod ?? meta.payment_method;
                                const cn = meta.chequeNumber ?? meta.cheque_number;
                                payments.push({ amount: amt, method: pm === 'cheque' ? (cn ? `Cheque #${cn}` : 'Cheque') : (pm || '—') });
                              }
                            }
                            paymentsAfterByIndex[i] = payments;
                          }
                          return ledger.map((entry, index) => {
                          const dr = parseFloat(entry.debit) || 0;
                          const cr = parseFloat(entry.credit) || 0;
                          const amount = dr > 0 ? dr : cr;
                          const billRef = getStatementBillRef(entry);
                          return (
                          <React.Fragment key={entry.id}>
                            <tr>
                              <td>{new Date(entry.transaction_date).toLocaleDateString()}</td>
                              <td><span className={`type-badge ${(entry.reference_type === 'sale_return' ? 'sale_return' : (entry.transaction_type || entry.entry_type_label || '').replace(/\s+/g, '_'))}`}>{entry.entry_type_label || (entry.transaction_type === 'balance_adjustment' ? 'Balance edited' : (entry.transaction_type || '').replace(/_/g, ' ')) || '—'}</span></td>
                              <td className="statement-bill-ref">{billRef}</td>
                              <td>
                                <div>{entry.description || '—'}</div>
                                {entry.sale_details && !entry.sale_details.items?.length && <div className="statement-ref">Sale #{entry.sale_details.invoice_number} · {formatPKR(entry.sale_details.total_amount)}</div>}
                                {entry.purchase_details && !entry.purchase_details.items?.length && <div className="statement-ref">Purchase #{entry.purchase_details.purchase_number} · {formatPKR(entry.purchase_details.total_amount)}</div>}
                                {entry.sale_return_details && <div className="statement-ref">Refund payment paid for sale return #{entry.sale_return_details.return_number} · {formatPKR(entry.sale_return_details.refund_amount)}</div>}
                                {(entry.sale_details || entry.purchase_details) && paymentsAfterByIndex[index]?.length > 0 && (
                                  <div className="statement-payments-against">
                                    <strong>Payments against this invoice:</strong>{' '}
                                    {paymentsAfterByIndex[index].map((p, i) => (
                                      <span key={i}>{formatPKR(p.amount)} ({p.method}){i < paymentsAfterByIndex[index].length - 1 ? ', ' : ''}</span>
                                    ))}
                                  </div>
                                )}
                                {(entry.sale_details || entry.purchase_details) && (!paymentsAfterByIndex[index] || paymentsAfterByIndex[index].length === 0) && (
                                  <div className="statement-payments-against statement-payments-against--none">—</div>
                                )}
                              </td>
                              <td className="debit amount-cell">{dr > 0 ? formatPKR(dr) : '—'}</td>
                              <td className="credit amount-cell">{cr > 0 ? formatPKR(cr) : '—'}</td>
                              <td className="payment-method-cell">
                                {(() => {
                                  const meta = entry.meta || {};
                                  const ref = getPaymentReferenceFromMeta(meta);
                                  const refNode = ref ? <span className="statement-payment-ref"> · Ref: {ref}</span> : null;
                                  const pm = meta.paymentMethod ?? meta.payment_method;
                                  const chequeNum = meta.chequeNumber ?? meta.cheque_number;
                                  const splits = meta.paymentSplits ?? meta.payment_splits;
                                  if (pm === 'cheque') return <><span className="statement-payment-tag cheque">Cheque{chequeNum ? ` #${chequeNum}` : ''}</span>{refNode}</>;
                                  if (pm === 'cash') return <><span className="statement-payment-tag cash">Cash</span>{refNode}</>;
                                  if (pm === 'split' && Array.isArray(splits) && splits.length) {
                                    const parts = splits.map(s => (s.paymentMethod ?? s.payment_method) === 'cheque' ? `Cheque${(s.chequeNumber ?? s.cheque_number) ? ` #${s.chequeNumber ?? s.cheque_number}` : ''}` : (s.paymentMethod ?? s.payment_method)).filter(Boolean);
                                    return <><span className="statement-payment-tag split">{parts.length ? parts.join(', ') : 'Split'}</span>{refNode}</>;
                                  }
                                  if (pm) return <><span className="statement-payment-tag">{pm}</span>{refNode}</>;
                                  if ((dr > 0 || cr > 0) && (entry.transaction_type === 'payment_in' || entry.transaction_type === 'payment_out') && ref) {
                                    return <span className="statement-payment-ref">Ref: {ref}</span>;
                                  }
                                  if ((dr > 0 || cr > 0) && (entry.transaction_type === 'payment_in' || entry.transaction_type === 'payment_out')) return '—';
                                  return null;
                                })()}
                              </td>
                              <td className="balance-cell">{statementData.party?.is_shop_expense ? '—' : formatBalance(entry.balance_after)}</td>
                            </tr>
                            {entry.sale_details && entry.sale_details.items && entry.sale_details.items.length > 0 && (
                              <tr className="statement-detail-row">
                                <td colSpan={8}>
                                  <div className="statement-bill-caption">Sale #{entry.sale_details.invoice_number || entry.reference_id} – Line items</div>
                                  <table className="statement-items-table">
                                    <thead><tr><th>Product</th><th>Code</th><th>Variant</th><th>Qty</th><th>Unit</th><th>Amount</th></tr></thead>
                                    <tbody>
                                      {entry.sale_details.items.map((item, idx) => (
                                        <tr key={idx}>
                                          <td>{item.product_name}</td>
                                          <td>{item.product_code || '—'}</td>
                                          <td>{item.variant_name || item.variant_sku || '—'}</td>
                                          <td>{item.quantity}</td>
                                          <td>{formatPKR(item.unit_price)}</td>
                                          <td>{formatPKR(item.total_price)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    <tfoot><tr><td colSpan={5}>Sale total</td><td>{formatPKR(entry.sale_details.total_amount)}</td></tr></tfoot>
                                  </table>
                                </td>
                              </tr>
                            )}
                            {entry.purchase_details && entry.purchase_details.items && entry.purchase_details.items.length > 0 && (
                              <tr className="statement-detail-row">
                                <td colSpan={8}>
                                  <div className="statement-bill-caption">Purchase #{entry.purchase_details.purchase_number || entry.reference_id} – Line items</div>
                                  <table className="statement-items-table">
                                    <thead><tr><th>Product</th><th>Code</th><th>Variant</th><th>Qty</th><th>Unit</th><th>Amount</th></tr></thead>
                                    <tbody>
                                      {entry.purchase_details.items.map((item, idx) => (
                                        <tr key={idx}>
                                          <td>{item.product_name}</td>
                                          <td>{item.product_code || '—'}</td>
                                          <td>{item.variant_name || item.variant_sku || '—'}</td>
                                          <td>{item.quantity}</td>
                                          <td>{formatPKR(item.unit_price)}</td>
                                          <td>{formatPKR(item.total_price)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    <tfoot><tr><td colSpan={5}>Purchase total</td><td>{formatPKR(entry.purchase_details.total_amount)}</td></tr></tfoot>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                          );
                        }); })()}
                      </tbody>
                    </table>
                  </div>
                  {(statementData.ledger || []).length === 0 && (
                    <p className="no-entries">No transactions in this period. If you added purchases from this party, confirm them (Purchases → open the purchase → Confirm) so they appear here.</p>
                  )}
                  <div className="statement-footer">
                    <strong>Current balance:</strong> {formatBalance(statementData.party?.balance, statementData.party?.is_shop_expense)}
                  </div>
                </div>
              ) : (
                <div className="empty-state">Select period and click Apply to load statement.</div>
              )}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => handlePrintStatement(statementParty, statementData)}
                  disabled={statementPdfLoading || !statementData}
                  title={!statementData ? 'Load statement (Apply) first' : 'Print statement (opens print dialog)'}
                >
                  <HiPrinter /> {statementPdfLoading ? 'Preparing…' : 'Print statement'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => handleDownloadPdfStatement(statementParty, statementData)}
                  disabled={statementPdfLoading || !statementData}
                  title={!statementData ? 'Load statement (Apply) first' : 'Download statement as PDF'}
                >
                  <HiDocumentReport /> {statementPdfLoading ? 'Preparing…' : 'Download PDF'}
                </button>
                <button type="button" onClick={() => setShowStatementModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Recording Modal */}
        {showPaymentModal && selectedParty && (
          <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
            <div className="modal-content payment-modal" onClick={(e) => e.stopPropagation()}>
              <h2>Record Payment - {selectedParty.name}</h2>
              <div className="payment-info">
                <p><strong>Current Balance:</strong> {formatBalance(selectedParty.balance, selectedParty.is_shop_expense)}</p>
              </div>
              <form onSubmit={handlePaymentSubmit}>
                <div className={`form-group total-amount-by-type ${paymentFormData.paymentType === 'payment_out' ? 'total-amount-payment-made' : 'total-amount-payment-received'}`}>
                  <label>Total Payment Amount (PKR) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={paymentFormData.amount}
                    onChange={(e) => {
                      const newAmount = e.target.value;
                      const newSplits = [...paymentFormData.paymentSplits];
                      // Auto-fill first split if only one split exists
                      if (newSplits.length === 1 && !newSplits[0].amount) {
                        newSplits[0].amount = newAmount;
                      }
                      setPaymentFormData({ ...paymentFormData, amount: newAmount, paymentSplits: newSplits });
                    }}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Payment Type *</label>
                  {selectedParty?.is_shop_expense ? (
                    <div className="shop-expense-payment-type-hint payment-type-option payment-made selected">
                      <span className="payment-type-label">Payment Made</span>
                    </div>
                  ) : (
                    <div className="payment-type-options">
                      <label className={`payment-type-option payment-made ${paymentFormData.paymentType === 'payment_out' ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={paymentFormData.paymentType === 'payment_out'}
                          onChange={() => setPaymentFormData({ ...paymentFormData, paymentType: 'payment_out' })}
                          className="payment-type-checkbox"
                        />
                        <span className="payment-type-label">Payment Made</span>
                      </label>
                      <label className={`payment-type-option payment-received ${paymentFormData.paymentType === 'payment_in' ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={paymentFormData.paymentType === 'payment_in'}
                          onChange={() => setPaymentFormData({ ...paymentFormData, paymentType: 'payment_in' })}
                          className="payment-type-checkbox"
                        />
                        <span className="payment-type-label">Payment Received</span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Payment Splits */}
                <div className="form-group">
                  <div className="payment-splits-header">
                    <label>Payment Splits *</label>
                    <button
                      type="button"
                      className="btn-add-split"
                      onClick={() => {
                        const defaultCashAccount = fundAccounts.find(a => a.type === 'cash');
                        setPaymentFormData({
                          ...paymentFormData,
                          paymentSplits: [...paymentFormData.paymentSplits, { 
                            paymentMethod: 'cash', 
                            accountId: defaultCashAccount ? String(defaultCashAccount.id) : '', 
                            amount: '', 
                            chequeNumber: '', 
                            chequeDate: '', 
                            referenceNumber: '' 
                          }]
                        });
                      }}
                    >
                      <HiPlus /> Add Split
                    </button>
                  </div>
                  <small className="payment-splits-hint">
                    Split payment across multiple methods/accounts (e.g., part cash, part cheque, part bank transfer)
                  </small>
                  {paymentFormData.paymentSplits.map((split, idx) => {
                    const isCheque = split.paymentMethod === 'cheque';
                    const isCash = split.paymentMethod === 'cash';
                    const filteredAccounts = isCash 
                      ? fundAccounts.filter(a => a.type === 'cash')
                      : fundAccounts.filter(a => a.type === 'bank');
                    return (
                      <div key={idx} className="payment-split-card">
                        <div className="payment-split-card-header">
                          <strong>Split {idx + 1}</strong>
                          {paymentFormData.paymentSplits.length > 1 && (
                            <button
                              type="button"
                              className="btn-remove-split"
                              onClick={() => setPaymentFormData({
                                ...paymentFormData,
                                paymentSplits: paymentFormData.paymentSplits.filter((_, i) => i !== idx)
                              })}
                              aria-label="Remove split"
                            >
                              <HiX />
                            </button>
                          )}
                        </div>
                        <div className="payment-split-grid">
                          <div className="form-group">
                            <label>Payment Method *</label>
                            <select
                              className="payment-method-select"
                              value={split.paymentMethod}
                              onChange={(e) => {
                                const newMethod = e.target.value;
                                const newSplits = [...paymentFormData.paymentSplits];
                                const isChequeNew = newMethod === 'cheque';
                                const isCashNew = newMethod === 'cash';
                                const filtered = isCashNew 
                                  ? fundAccounts.filter(a => a.type === 'cash')
                                  : fundAccounts.filter(a => a.type === 'bank');
                                const defaultAccount = isCashNew 
                                  ? filtered.find(a => a.type === 'cash')
                                  : filtered[0];
                                const currentAccount = fundAccounts.find(a => String(a.id) === newSplits[idx].accountId);
                                const shouldResetAccount = !isChequeNew && currentAccount && (
                                  (isCashNew && currentAccount.type !== 'cash') || 
                                  (!isCashNew && currentAccount.type !== 'bank')
                                );
                                newSplits[idx] = { 
                                  ...newSplits[idx], 
                                  paymentMethod: newMethod, 
                                  accountId: isChequeNew ? '' : (shouldResetAccount ? (defaultAccount ? String(defaultAccount.id) : '') : newSplits[idx].accountId),
                                  chequeNumber: '', 
                                  chequeDate: '', 
                                  referenceNumber: '' 
                                };
                                setPaymentFormData({ ...paymentFormData, paymentSplits: newSplits });
                              }}
                              required
                            >
                              <option value="cash">Cash</option>
                              <option value="bank_transfer">Bank Transfer</option>
                              <option value="cheque">Cheque</option>
                              <option value="card">Card</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          {isCheque ? (
                            <>
                              {paymentFormData.paymentType === 'payment_out' ? (
                                <div className="form-group">
                                  <label>Draw from bank *</label>
                                  <AccountSelect
                                    accounts={fundAccounts.filter(a => a.type === 'bank')}
                                    value={split.accountId}
                                    onChange={(id) => {
                                      const newSplits = [...paymentFormData.paymentSplits];
                                      newSplits[idx] = { ...newSplits[idx], accountId: String(id) };
                                      setPaymentFormData({ ...paymentFormData, paymentSplits: newSplits });
                                    }}
                                    placeholder="Select bank account"
                                    id={`payment-split-account-${idx}`}
                                  />
                                  {split.accountId && (parseFloat(split.amount) || 0) > 0 && (() => {
                                    const acc = fundAccounts.find(a => String(a.id) === split.accountId);
                                    const bal = acc && (acc.current_balance != null || acc.currentBalance != null) ? (parseFloat(acc.current_balance ?? acc.currentBalance) || 0) : null;
                                    const amt = parseFloat(split.amount) || 0;
                                    if (bal === null || bal === undefined) {
                                      return (
                                        <p className="payment-split-balance-warn" role="alert">
                                          Account balance unavailable. You can still record the cheque.
                                        </p>
                                      );
                                    }
                                    if (bal < amt) {
                                      return (
                                        <p className="payment-split-balance-warn" role="alert">
                                          This account does not have this amount (balance: {formatPKR(bal)}). You can still record the cheque.
                                        </p>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              ) : (
                                <div className="form-group payment-split-cheque-note">
                                  <span className="payment-split-cheque-hint">Cheque will appear in Pending or Upcoming. When you clear it, select the account to deposit into (Accounts → Pending cheques → Approve).</span>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="form-group">
                              <label>Deposit to account *</label>
                              <AccountSelect
                                accounts={filteredAccounts}
                                value={split.accountId}
                                onChange={(id) => {
                                  const newSplits = [...paymentFormData.paymentSplits];
                                  newSplits[idx] = { ...newSplits[idx], accountId: String(id) };
                                  setPaymentFormData({ ...paymentFormData, paymentSplits: newSplits });
                                }}
                                placeholder="Select account"
                                id={`payment-split-account-${idx}`}
                              />
                            </div>
                          )}
                        </div>
                        <div className="form-group">
                          <label>Amount (PKR) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={split.amount}
                            onChange={(e) => {
                              const newSplits = [...paymentFormData.paymentSplits];
                              newSplits[idx] = { ...newSplits[idx], amount: e.target.value };
                              setPaymentFormData({ ...paymentFormData, paymentSplits: newSplits });
                            }}
                            placeholder="0.00"
                            required
                          />
                        </div>
                        {split.paymentMethod === 'cheque' ? (
                          <div className="payment-split-grid">
                            <div className="form-group">
                              <label>Cheque Number</label>
                              <input
                                type="text"
                                value={split.chequeNumber}
                                onChange={(e) => {
                                  const newSplits = [...paymentFormData.paymentSplits];
                                  newSplits[idx] = { ...newSplits[idx], chequeNumber: e.target.value };
                                  setPaymentFormData({ ...paymentFormData, paymentSplits: newSplits });
                                }}
                                placeholder="Cheque #"
                              />
                            </div>
                            <div className="form-group">
                              <label>Cheque Date</label>
                              <input
                                type="date"
                                value={split.chequeDate}
                                onChange={(e) => {
                                  const newSplits = [...paymentFormData.paymentSplits];
                                  newSplits[idx] = { ...newSplits[idx], chequeDate: e.target.value };
                                  setPaymentFormData({ ...paymentFormData, paymentSplits: newSplits });
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="form-group">
                            <label>Reference Number</label>
                            <input
                              type="text"
                              value={split.referenceNumber}
                              onChange={(e) => {
                                const newSplits = [...paymentFormData.paymentSplits];
                                newSplits[idx] = { ...newSplits[idx], referenceNumber: e.target.value };
                                setPaymentFormData({ ...paymentFormData, paymentSplits: newSplits });
                              }}
                              placeholder="Transaction ID, etc."
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(() => {
                    if (!paymentFormData.amount) return null;
                    const splitsSum = paymentFormData.paymentSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
                    const remaining = parseFloat(paymentFormData.amount) - splitsSum;
                    const ok = Math.abs(remaining) < 0.01;
                    return (
                      <div className="payment-splits-summary">
                        <div className="payment-splits-summary-row">
                          <span>Splits Total:</span>
                          <strong>{formatPKR(splitsSum)}</strong>
                        </div>
                        <div className={`payment-splits-summary-row ${ok ? 'remaining-ok' : 'remaining-warn'}`}>
                          <span>Remaining:</span>
                          <strong>{formatPKR(remaining)}</strong>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="form-group">
                  <label>
                    Description {selectedParty?.is_shop_expense && paymentFormData.paymentType === 'payment_out' && <span className="required">*</span>}
                  </label>
                  <textarea
                    value={paymentFormData.description}
                    onChange={(e) => setPaymentFormData({ ...paymentFormData, description: e.target.value })}
                    placeholder={selectedParty?.is_shop_expense && paymentFormData.paymentType === 'payment_out' ? 'What is this payment for? (e.g. electricity, rent, packing material)' : 'Additional payment details...'}
                    rows="3"
                  />
                  {selectedParty?.is_shop_expense && paymentFormData.paymentType === 'payment_out' && (
                    <small className="form-hint">Required for shop expense: describe what the payment was for.</small>
                  )}
                </div>
                <div className="form-group">
                  <label>Transaction Date *</label>
                  <input
                    type="date"
                    value={paymentFormData.transactionDate}
                    onChange={(e) => setPaymentFormData({ ...paymentFormData, transactionDate: e.target.value })}
                    required
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => setShowPaymentModal(false)}>Cancel</button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={
                      paymentSubmitting ||
                      (() => {
                        const totalAmount = parseFloat(paymentFormData.amount) || 0;
                        const splitsSum = paymentFormData.paymentSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
                        return totalAmount > 0 && Math.abs(splitsSum - totalAmount) > 0.01;
                      })()
                    }
                    title={(() => {
                      if (paymentSubmitting) return 'Recording payment…';
                      const totalAmount = parseFloat(paymentFormData.amount) || 0;
                      const splitsSum = paymentFormData.paymentSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
                      if (totalAmount > 0 && Math.abs(splitsSum - totalAmount) > 0.01) {
                        return `Splits total (${formatPKR(splitsSum)}) must equal payment amount (${formatPKR(totalAmount)})`;
                      }
                      return '';
                    })()}
                  >
                    {paymentSubmitting ? 'Recording…' : 'Record Payment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Pay Salary Modal */}
        {showSalaryModal && selectedParty && (
          <div className="modal-overlay" onClick={() => setShowSalaryModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Pay Salary – {selectedParty.name}</h2>
              {selectedParty.salary && <p className="form-hint">Salary: {formatPKR(selectedParty.salary)}/month</p>}
              <form onSubmit={handleSalarySubmit}>
                <div className="form-group">
                  <label>Amount *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={salaryFormData.amount}
                    onChange={(e) => setSalaryFormData({ ...salaryFormData, amount: e.target.value })}
                    placeholder={selectedParty.salary ? String(selectedParty.salary) : 'Enter amount'}
                    required
                  />
                  <small className="form-hint">Full or partial salary payment</small>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Salary for month *</label>
                    <select
                      value={salaryFormData.periodMonth}
                      onChange={(e) => setSalaryFormData({ ...salaryFormData, periodMonth: e.target.value })}
                      required
                    >
                      <option value="">Select month</option>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <option key={m} value={m}>{new Date(2000, m-1).toLocaleString('default', { month: 'short' })}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Salary for year *</label>
                    <select
                      value={salaryFormData.periodYear}
                      onChange={(e) => setSalaryFormData({ ...salaryFormData, periodYear: e.target.value })}
                      required
                    >
                      <option value="">Select year</option>
                      {(() => {
                        const y = new Date().getFullYear();
                        const years = [];
                        for (let i = y + 1; i >= 2015; i--) years.push(i);
                        return years.map(yr => <option key={yr} value={yr}>{yr}</option>);
                      })()}
                    </select>
                  </div>
                </div>
                <small className="form-hint">Choose the month/year this salary is for. Use a past month to record skipped salary.</small>
                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={salaryFormData.description}
                    onChange={(e) => setSalaryFormData({ ...salaryFormData, description: e.target.value })}
                    placeholder="e.g. Part 1 of 2 for Jan 2025"
                  />
                </div>
                <div className="form-group">
                  <label>Payment Method</label>
                  <select value={salaryFormData.paymentMethod} onChange={(e) => setSalaryFormData({ ...salaryFormData, paymentMethod: e.target.value })}>
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="card">Card</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Account (Cash / Bank)</label>
                  <select value={salaryFormData.accountId} onChange={(e) => setSalaryFormData({ ...salaryFormData, accountId: e.target.value })}>
                    <option value="">Default (Cash)</option>
                    {fundAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.name} ({a.type})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={salaryFormData.transactionDate}
                    onChange={(e) => setSalaryFormData({ ...salaryFormData, transactionDate: e.target.value })}
                    required
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => setShowSalaryModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary">Pay Salary</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Record Advance Modal */}
        {showAdvanceModal && selectedParty && (
          <div className="modal-overlay" onClick={() => setShowAdvanceModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Record Advance – {selectedParty.name}</h2>
              <form onSubmit={handleAdvanceSubmit}>
                <div className="form-group">
                  <label>Amount *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={advanceFormData.amount}
                    onChange={(e) => setAdvanceFormData({ ...advanceFormData, amount: e.target.value })}
                    placeholder="Enter amount"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Account (Cash / Bank)</label>
                  <select value={advanceFormData.accountId} onChange={(e) => setAdvanceFormData({ ...advanceFormData, accountId: e.target.value })}>
                    <option value="">Default (Cash)</option>
                    {fundAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.name} ({a.type})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={advanceFormData.description}
                    onChange={(e) => setAdvanceFormData({ ...advanceFormData, description: e.target.value })}
                    placeholder="e.g. Advance against Jan 2025 salary"
                  />
                </div>
                <div className="form-group">
                  <label>Payment Method</label>
                  <select value={advanceFormData.paymentMethod} onChange={(e) => setAdvanceFormData({ ...advanceFormData, paymentMethod: e.target.value })}>
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="card">Card</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={advanceFormData.transactionDate}
                    onChange={(e) => setAdvanceFormData({ ...advanceFormData, transactionDate: e.target.value })}
                    required
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => setShowAdvanceModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary">Record Advance</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => { setShowConfirmModal(false); setPartyToDelete(null); }}
          onConfirm={confirmDelete}
          title="Delete Party"
          message="Are you sure you want to delete this party? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />
      </div>
    </Layout>
  );
};

export default Parties;
